import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import adminApi from "../../services/adminApi";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import {
  FiUsers, FiUserCheck, FiFrown, FiUserX, FiAlertTriangle,
  FiSearch, FiDownload, FiUserPlus, FiRefreshCw, FiColumns,
  FiEye, FiBell, FiMoreHorizontal, FiX, FiCheck, FiBox,
  FiArrowUp, FiList, FiGrid, FiMenu, FiTrendingUp, FiFeather, FiGlobe,
  FiChevronDown, FiUser, FiFilter, FiHeart, FiCreditCard, FiLayers,
  FiMail, FiPhone, FiZap, FiCalendar, FiActivity, FiTrash2, FiClock,
  FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight
} from "react-icons/fi";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  olive:"#6B8A5A", olive2:"#8BA47A",
  orange:"#F29A0C", orange2:"#E97818",
  dark:"#1F1F1F", mid:"#555", muted:"#AAA", light:"#CCC",
  border:"rgba(239,229,216,0.8)",
  green:"#22C55E", greenDk:"#16A34A",
  red:"#E24B4A", amber:"#F59E0B",
};
const glass = {
  background:"rgba(255,255,255,0.82)",
  border:"1px solid rgba(255,255,255,0.95)",
  backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)",
  boxShadow:"0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const AVATAR_GRADS = [
  "linear-gradient(135deg,#F29A0C,#E97818)",
  "linear-gradient(135deg,#6B8A5A,#8BA47A)",
  "linear-gradient(135deg,#534AB7,#7F77DD)",
  "linear-gradient(135deg,#185FA5,#378ADD)",
  "linear-gradient(135deg,#E24B4A,#A32D2D)",
  "linear-gradient(135deg,#0F6E56,#1D9E75)",
];
const TIER_STYLES = {
  Premium:     { bg:"rgba(242,154,12,0.12)", c:"#C8660A", b:"rgba(242,154,12,0.25)" },
  Standard:    { bg:"rgba(83,74,183,0.10)",  c:"#3C3489", b:"rgba(83,74,183,0.20)"  },
  Basic:       { bg:"rgba(24,95,165,0.10)",  c:"#0C447C", b:"rgba(24,95,165,0.20)"  },
  "YTT Pro":   { bg:"rgba(107,138,90,0.12)", c:"#27500A", b:"rgba(107,138,90,0.25)" },
  Pro:         { bg:"rgba(107,138,90,0.12)", c:"#27500A", b:"rgba(107,138,90,0.25)" },
  "Free Trial":{ bg:"rgba(200,200,200,0.12)",c:"#666",    b:"rgba(200,200,200,0.3)" },
  Subscriber:  { bg:"rgba(107,138,90,0.12)", c:"#27500A", b:"rgba(107,138,90,0.25)" },
  Expired:     { bg:"rgba(226,75,74,0.12)",  c:"#DC2626", b:"rgba(226,75,74,0.25)" },
};
const STATUS_COLORS = {
  Active:"#16A34A", Inactive:"#F59E0B", Deactivated:"#F59E0B", Expired:"#DC2626", Pending:"#185FA5", Suspended:"#E24B4A",
};
const TIERS = ["all","Premium","Standard","Basic","Pro","Free Trial","Subscriber"];
const PAGE_SIZE = 10;

// ─── NORMALISE API RESPONSE (handles both camelCase and PascalCase) ───────────
const norm = (u) => ({
  id:                   u.id                   ?? u.Id,
  userName:             u.userName             ?? u.UserName             ?? "",
  fullName:             u.fullName             ?? u.FullName             ?? "",
  email:                u.email                ?? u.Email                ?? "",
  phone:                u.phone                ?? u.Phone                ?? "—",
  gender:               u.gender               ?? u.Gender               ?? "—",
  profileImageUrl:      u.profileImageUrl      ?? u.ProfileImageUrl      ?? null,
  address:              u.address              ?? u.Address              ?? "",
  planName:             u.planName             ?? u.PlanName             ?? null,
  tier:                 u.tier                 ?? u.Tier                 ?? null,
  daysLeft:             u.daysLeft             ?? u.DaysLeft             ?? 0,
  subscriptionEndDate:  u.subscriptionEndDate  ?? u.SubscriptionEndDate  ?? null,
  subscriptionStatus:   u.subscriptionStatus   ?? u.SubscriptionStatus   ?? null,
  isSubscriptionExpired:u.isSubscriptionExpired?? u.IsSubscriptionExpired?? false,
  isActive:             u.isActive             ?? u.IsActive             ?? false,
  hasActiveSubscription:u.hasActiveSubscription?? u.HasActiveSubscription?? false,
  sessions:             u.sessions             ?? u.Sessions             ?? 0,
  streak:               u.streak               ?? u.Streak               ?? 0,
  isOnline:             u.isOnline             ?? u.IsOnline             ?? false,
  lastLogin:            u.lastLogin            ?? u.LastLogin            ?? null,
  createdAt:            u.createdAt            ?? u.CreatedAt,
});

const normStats = (d) => ({
  totalUsers:          Number(d?.totalUsers ?? d?.TotalUsers ?? 0),
  activeUsers:         Number(d?.activeUsers ?? d?.ActiveUsers ?? 0),
  expiredUsers:        Number(d?.expiredUsers ?? d?.ExpiredUsers ?? d?.expiredSubscriptions ?? d?.ExpiredSubscriptions ?? 0),
  inactive14dUsers:    Number(d?.inactive14dUsers ?? d?.Inactive14dUsers ?? d?.inactiveUsers ?? d?.InactiveUsers ?? 0),
  deactivatedUsers:    Number(d?.deactivatedUsers ?? d?.DeactivatedUsers ?? 0),
  activeSubscriptions: Number(d?.activeSubscriptions ?? d?.ActiveSubscriptions ?? 0),
  newUsersThisMonth:   Number(d?.newUsersThisMonth ?? d?.NewUsersThisMonth ?? 0),
});

// Location details parser from Address
const parseLocation = (addr) => {
  if (!addr) return { area: "N/A", city: "N/A", state: "N/A", country: "India" };
  
  const parts = addr.split(",").map(p => p.trim()).filter(Boolean);
  const len = parts.length;
  
  if (len === 0) return { area: "N/A", city: "N/A", state: "N/A", country: "India" };
  
  const INDIAN_STATES = [
    "andhra pradesh", "arunachal pradesh", "assam", "bihar", "chhattisgarh", "goa", "gujarat", 
    "haryana", "himachal pradesh", "jharkhand", "karnataka", "kerala", "madhya pradesh", 
    "maharashtra", "manipur", "meghalaya", "mizoram", "nagaland", "odisha", "punjab", 
    "rajasthan", "sikkim", "tamil nadu", "telangana", "tripura", "uttar pradesh", 
    "uttarakhand", "west bengal", "delhi", "puducherry", "jammu & kashmir", "ladakh", 
    "chandigarh", "lakshadweep", "andaman and nicobar"
  ];
  
  let country = "India";
  let state = "N/A";
  let city = "N/A";
  let area = "N/A";
  
  if (len === 1) {
    const token = parts[0];
    if (INDIAN_STATES.includes(token.toLowerCase())) {
      state = token;
    } else {
      city = token;
    }
    return { area, city, state, country };
  }
  
  const lastToken = parts[len - 1];
  let countryIndex = len - 1;
  if (lastToken.toLowerCase() === "india" || lastToken.toLowerCase() === "ind") {
    country = lastToken;
    countryIndex = len - 2;
  }
  
  if (countryIndex < 0) {
    return { area, city: parts[0], state, country };
  }
  
  const potentialState = parts[countryIndex];
  if (INDIAN_STATES.includes(potentialState.toLowerCase())) {
    state = potentialState;
    if (countryIndex - 1 >= 0) {
      city = parts[countryIndex - 1];
    }
    if (countryIndex - 2 >= 0) {
      area = parts.slice(0, countryIndex - 1).join(", ");
    }
  } else {
    city = potentialState;
    if (city.toLowerCase().includes("bangalore") || city.toLowerCase().includes("bengaluru") || city.toLowerCase().includes("mysore")) {
      state = "Karnataka";
    } else if (city.toLowerCase().includes("chennai")) {
      state = "Tamil Nadu";
    } else if (city.toLowerCase().includes("delhi") || city.toLowerCase().includes("noida")) {
      state = "Delhi";
    }
    
    if (countryIndex - 1 >= 0) {
      area = parts.slice(0, countryIndex).join(", ");
    }
  }
  
  return { area, city, state, country };
};

// ─── MAP API USER → UI SHAPE ─────────────────────────────────────────────────
const mapUser = (raw, idx) => {
  const u = norm(raw);
  const fullName = u.fullName || "Unknown";
  const loc = parseLocation(u.address);
  const countryName = loc.country === "N/A" ? "India" : loc.country;
  const isExp = Boolean(u.isSubscriptionExpired || u.subscriptionStatus === "Expired");
  const resolvedPlan = u.planName || u.tier || (isExp ? "Expired Plan" : (u.hasActiveSubscription ? "Active Plan" : "Free Trial"));
  
  let resolvedStatus = "Active";
  if (!u.isActive) {
    resolvedStatus = "Deactivated";
  } else if (isExp) {
    resolvedStatus = "Expired";
  } else if (u.hasActiveSubscription) {
    resolvedStatus = "Active";
  } else {
    resolvedStatus = "Active";
  }

  return {
    id:       u.id,
    name:     fullName,
    initials: fullName.split(" ").filter(Boolean).slice(0,2).map(w=>w[0]).join("").toUpperCase() || "U",
    email:    u.email,
    phone:    u.phone,
    gender:   u.gender,
    profileImageUrl: u.profileImageUrl,
    userName: u.userName,
    address:  u.address,
    country:  countryName,
    tier:     resolvedPlan,
    planName: resolvedPlan,
    daysLeft: u.daysLeft,
    subscriptionEndDate: u.subscriptionEndDate,
    subscriptionStatus: u.subscriptionStatus || (isExp ? "Expired" : "Active"),
    isExpired: isExp,
    status:   resolvedStatus,
    online:   u.isOnline,
    sessions: Number(u.sessions || 0),
    streak:   Number(u.streak || 0),
    wellness: (Number(u.sessions || 0) === 0 && Number(u.streak || 0) === 0) 
      ? 0 
      : Math.min(100, Math.min(45, Number(u.streak || 0) * 5) + Math.min(45, Number(u.sessions || 0) * 5) + (u.hasActiveSubscription ? 10 : 0)),
    progress: isExp ? 0 : Math.min(100, Math.round(((Number(u.sessions || 0)) / 20) * 100)),
    joined:   u.createdAt
      ? new Date(u.createdAt).toLocaleDateString("en-GB",{ day:"2-digit", month:"short", year:"numeric" })
      : "—",
    lastLogin: u.lastLogin
      ? new Date(u.lastLogin).toLocaleDateString("en-GB",{ day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })
      : (u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-GB",{ day:"2-digit", month:"short", year:"numeric" }) : "—"),
    lang:     "—",
    grad:     AVATAR_GRADS[idx % AVATAR_GRADS.length],
    isActive: u.isActive,
  };
};

// ─── SMALL COMPONENTS (UI unchanged) ─────────────────────────────────────────
function TierPill({ tier, daysLeft, isExpired }) {
  const displayTier = tier || "Free Trial";
  const isExp = isExpired || displayTier.toLowerCase().includes("expired");

  if (isExp) {
    return (
      <div className="inline-flex flex-col items-start gap-0.5">
        <span className="inline-flex items-center text-[10.5px] font-black px-2.5 py-0.5 rounded-full tracking-[0.3px]"
          style={{ background:"rgba(220,38,38,0.10)", color:"#DC2626", border:"1px solid rgba(220,38,38,0.25)" }}>
          {displayTier.includes("Expired") ? displayTier : `${displayTier} (Expired)`}
        </span>
        <span className="text-[9px] font-bold text-red-500 pl-0.5">
          Plan Expired
        </span>
      </div>
    );
  }

  const matchKey = Object.keys(TIER_STYLES).find(k => displayTier?.toLowerCase().includes(k.toLowerCase()));
  let s = TIER_STYLES[displayTier] || (matchKey ? TIER_STYLES[matchKey] : null);
  if (!s) {
    const isFree = displayTier.toLowerCase().includes("free") || displayTier.toLowerCase().includes("trial");
    s = isFree
      ? TIER_STYLES["Free Trial"]
      : { bg:"rgba(107,138,90,0.12)", c:"#27500A", b:"rgba(107,138,90,0.25)" };
  }
  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <span className="inline-flex items-center text-[10.5px] font-black px-2.5 py-0.5 rounded-full tracking-[0.3px]"
        style={{ background:s.bg, color:s.c, border:`1px solid ${s.b}` }}>
        {displayTier}
      </span>
      {daysLeft > 0 && !displayTier.toLowerCase().includes("free") && (
        <span className="text-[9px] font-bold text-zinc-400 pl-0.5">
          {daysLeft}d left
        </span>
      )}
    </div>
  );
}
function StatusDot({ status }) {
  const label = status === "Inactive" ? "Deactivated" : status;
  const c = STATUS_COLORS[status] || STATUS_COLORS[label] || "#888";
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background:c }}/>
      <span className="text-[12px] font-semibold" style={{ color:c }}>{label}</span>
    </span>
  );
}
function UserAvatar({ user, size=34, radius=11 }) {
  const [imgError, setImgError] = useState(false);
  const imgUrl = user.profileImageUrl || user.avatar;

  return (
    <div className="relative flex-shrink-0" style={{ width:size, height:size }}>
      {imgUrl && !imgError ? (
        <img
          src={imgUrl}
          alt={user.name || "User"}
          onError={() => setImgError(true)}
          className="w-full h-full object-cover shadow-sm border border-zinc-200"
          style={{ borderRadius: radius }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center font-black text-white overflow-hidden"
          style={{ background:user.grad || AVATAR_GRADS[0], borderRadius:radius, fontFamily:"'Poppins',sans-serif", fontSize:size*0.32, position:"relative" }}>
          <div className="absolute inset-0" style={{ background:"linear-gradient(135deg,rgba(255,255,255,0.22),transparent)" }}/>
          <span className="relative z-10">{user.initials}</span>
        </div>
      )}
      {user.online && (
        <div className="absolute bottom-[2px] right-[2px] rounded-full border-[1.5px]"
          style={{ width:size*0.24, height:size*0.24, background:T.green, borderColor:"rgba(255,248,238,0.9)" }}/>
      )}
    </div>
  );
}
// ─── CUSTOM DROPDOWN COMPONENT (With Search & Dynamic Height) ───────────────────
function CustomDropdown({ value, onChange, options, label, icon: Icon, fullWidth = false, direction = "down", searchable = true, placeholder = "Select..." }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const selectedOption = options.find((o) => String(o.value) === String(value)) || options[0];

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter((o) =>
      String(o.label).toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  }, [options, searchTerm]);

  return (
    <div className={`relative ${fullWidth ? "w-full" : "inline-block"} text-left ${open ? "z-[500]" : "z-20"}`} ref={dropdownRef}>
      <motion.button
        type="button"
        whileTap={{ scale: 0.98 }}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center justify-between gap-2 ${fullWidth ? "w-full rounded-[13px] px-3.5 py-2.5" : "rounded-full px-4 py-2"} text-[12px] font-semibold border cursor-pointer outline-none transition-all shadow-sm`}
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.85)",
          borderColor: open ? T.olive : "rgba(239,229,216,0.8)",
          color: value ? "#1F1F1F" : T.mid,
          boxShadow: open ? "0 4px 20px rgba(107,138,90,0.18)" : "none",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-2 truncate min-w-0">
          {Icon && <Icon size={13} style={{ color: open ? T.olive : T.muted }} className="shrink-0" />}
          <span className="truncate">{selectedOption?.label || label || placeholder}</span>
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center ml-0.5 shrink-0"
        >
          <FiChevronDown size={14} style={{ color: open ? T.olive : T.muted }} />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: direction === "up" ? -8 : 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: direction === "up" ? -8 : 8, scale: 0.95 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className={`ad-dd-menu absolute left-0 ${direction === "up" ? "bottom-full mb-2" : "top-full mt-2"} ${fullWidth ? "w-full min-w-[180px]" : "min-w-[220px] max-w-[320px]"} max-h-[340px] rounded-[20px] p-2 z-[999] shadow-2xl text-left flex flex-col`}
            style={{
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid rgba(239,229,216,0.9)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
            }}
          >
            {/* Sticky Search Bar inside Dropdown Popover */}
            {searchable && options.length > 3 && (
              <div className="p-1 mb-1.5 border-b border-zinc-100/80 sticky top-0 bg-white/90 backdrop-blur-md z-10">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-100/90 border border-zinc-200/70 focus-within:border-[#6B8A5A] transition-all">
                  <FiSearch size={13} className="text-zinc-400 shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full text-[11.5px] bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(""); }}
                      className="text-zinc-400 hover:text-zinc-600 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {label && (
              <div className="px-2 py-1 mb-1 text-[10px] font-black uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                {label}
              </div>
            )}

            <div className="overflow-y-auto flex-1 max-h-[260px] pr-0.5" style={{ scrollbarWidth: "thin", msOverflowStyle: "none" }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-[11.5px] text-zinc-400 italic">
                  No matching items found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = String(opt.value) === String(value);
                  return (
                    <motion.button
                      key={String(opt.value)}
                      type="button"
                      whileHover={{ x: 3, background: "rgba(107,138,90,0.08)" }}
                      onClick={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold rounded-[12px] border-none bg-transparent cursor-pointer transition-all mb-0.5 text-left"
                      style={{
                        color: isSelected ? T.olive : "#333",
                        fontFamily: "'Inter', sans-serif",
                        background: isSelected ? "rgba(107,138,90,0.1)" : "transparent",
                      }}
                    >
                      <span className="flex items-center gap-2 truncate min-w-0">
                        {opt.dotColor && (
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: opt.dotColor }}
                          />
                        )}
                        <span className="truncate">{opt.label}</span>
                      </span>
                      {isSelected && <FiCheck size={13} style={{ color: T.olive }} className="shrink-0 ml-1" />}
                    </motion.button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StatCard({ icon:Icon, value, label, trend, trendUp=true, iconBg, accentColor, cardBg, delay=0 }) {
  return (
    <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay, duration:0.35, ease:[0.25,1,0.5,1] }}
      whileHover={{ y:-5, boxShadow:"0 20px 40px -10px rgba(0,0,0,0.12)" }}
      className="ad-kpi-card group relative overflow-hidden rounded-[24px] p-5 border transition-all duration-300 flex flex-col justify-between"
      style={{
        background: cardBg || "rgba(255, 255, 255, 0.88)",
        borderColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        boxShadow: "0 10px 30px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(239, 229, 216, 0.5)",
      }}>

      {/* Top ambient spotlight glow */}
      <div className="ad-kpi-orb absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-25 group-hover:opacity-50 transition-opacity duration-500 pointer-events-none"
        style={{ background: accentColor || iconBg, filter: "blur(28px)" }} />

      <div className="ad-kpi-body">
        {/* Header row: Icon + Trend badge */}
        <div className="ad-kpi-iconrow flex items-center justify-between mb-3.5">
          <div className="ad-kpi-icon w-11 h-11 rounded-[15px] flex items-center justify-center flex-shrink-0 text-white shadow-sm transition-transform duration-300 group-hover:scale-105"
            style={{ background: iconBg, boxShadow: "0 4px 14px rgba(0,0,0,0.1)" }}>
            <Icon size={18} />
          </div>

          <div className="ad-kpi-note flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border tracking-wide"
            style={{
              background: trendUp ? "rgba(34, 197, 94, 0.1)" : "rgba(226, 75, 74, 0.09)",
              color: trendUp ? T.greenDk : T.red,
              borderColor: trendUp ? "rgba(34, 197, 94, 0.2)" : "rgba(226, 75, 74, 0.2)",
              fontFamily: "'Inter', sans-serif"
            }}>
            <FiTrendingUp size={11} style={{ transform: trendUp ? "none" : "rotate(180deg)" }} />
            <span>{trend}</span>
          </div>
        </div>

        {/* Metric label */}
        <p className="ad-kpi-lbl text-[10px] font-black uppercase tracking-[1px] mb-1 text-zinc-400"
          style={{ fontFamily: "'Inter', sans-serif" }}>
          {label}
        </p>

        {/* Main value */}
        <p className="ad-kpi-val font-black text-[#1F1F1F] tracking-tight leading-none text-[30px]"
          style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: "-1.2px" }}>
          {value}
        </p>
      </div>

      {/* Sleek bottom accent bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-70 group-hover:opacity-100 transition-opacity duration-300 rounded-b-[24px]"
        style={{ background: accentColor }} />
    </motion.div>
  );
}

// ─── PROFILE DRAWER ───────────────────────────────────────────────────────────
function ProfileDrawer({ user, onClose, showToast, onStatusToggled, onNotify, onUserDeleted }) {
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    adminApi.getUser(user.id)
      .then(res => {
        if (res.success) {
          setDetails(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  // Calculate consecutive day check-in streak
  const calculateStreak = useCallback((attendances) => {
    if (!attendances || attendances.length === 0) return 0;
    
    // Extract unique dates sorted descending
    const dates = Array.from(new Set(
      attendances.map(a => new Date(a.attendanceDate).toDateString())
    )).map(d => new Date(d)).sort((a, b) => b - a);
    
    const today = new Date();
    today.setHours(0,0,0,0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let currentStreak = 0;
    let expectedDate = new Date(dates[0]); // start from the most recent check-in date
    expectedDate.setHours(0,0,0,0);
    
    // If the most recent check-in is older than yesterday, the streak is broken (0)
    if (expectedDate < yesterday) {
      return 0;
    }
    
    for (let i = 0; i < dates.length; i++) {
      const checkInDate = new Date(dates[i]);
      checkInDate.setHours(0,0,0,0);
      
      const diffTime = Math.abs(expectedDate - checkInDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        currentStreak++;
        expectedDate.setDate(expectedDate.getDate() - 1);
      } else if (diffDays === 1) {
        currentStreak++;
        expectedDate = new Date(checkInDate);
        expectedDate.setDate(expectedDate.getDate() - 1);
      } else {
        break;
      }
    }
    
    return currentStreak;
  }, []);

  const loc = details ? parseLocation(details.address) : { area: "Loading...", city: "Loading...", state: "Loading...", country: "Loading..." };

  // Decoupled stats binder (resolves dynamic sessions, streak, wellness, hours and progress)
  const detailsStats = useMemo(() => {
    if (!user) {
      return { sessions: 0, streak: 0, progress: 0, hours: 0, wellness: 0 };
    }
    const attendances = details?.attendances || [];
    const sessions = details?.totalAttendance !== undefined 
      ? Number(details.totalAttendance) 
      : (attendances.length || Number(user.sessions || 0));
    const streak = details?.streakDays !== undefined 
      ? Number(details.streakDays) 
      : Number(user.streak || 0);
    const progress = Math.min(100, Math.round((sessions / 20) * 100));
    const hours = Math.round(sessions * 0.75);
    const hasSub = Boolean(details?.subscriptions?.some(s => s.status === "Active") || user.hasActiveSubscription);
    const streakScore = Math.min(45, streak * 5);
    const sessionsScore = Math.min(45, sessions * 5);
    const subBonus = hasSub ? 10 : 0;
    const wellness = (sessions === 0 && streak === 0) 
      ? 0 
      : Math.min(100, streakScore + sessionsScore + subBonus);
    return { sessions, streak, progress, hours, wellness };
  }, [details, user]);

  if (!user) return null;

  const R = 52, C = 2 * Math.PI * R;

  const handleToggle = async () => {
    setToggling(true);
    try {
      await adminApi.toggleUserStatus(user.id);
      const msg = user.isActive ? `Deactivated: ${user.name}` : `Activated: ${user.name}`;
      showToast(msg);
      onStatusToggled(user.id);
      onClose();
    } catch (e) {
      showToast(e.message || "Failed to update status");
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteUser(user.id);
      showToast(`Permanently deleted user: ${user.name}`);
      setShowDeleteModal(false);
      onClose();
      if (onUserDeleted) onUserDeleted(user.id);
    } catch (e) {
      showToast(e.message || "Failed to delete user account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        onClick={onClose} className="fixed inset-0 z-[100]"
        style={{ background:"rgba(15,15,15,0.25)", backdropFilter:"blur(4px)" }}/>
      <motion.div initial={{ x:32, opacity:0 }} animate={{ x:0, opacity:1 }}
        exit={{ x:32, opacity:0 }} transition={{ duration:0.35, ease:[0.25,1,0.5,1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 overflow-y-auto z-[101] no-scrollbar [&::-webkit-scrollbar]:hidden"
        style={{ width:440, background:"rgba(255,253,248,0.97)", borderLeft:`1px solid rgba(239,229,216,0.7)`, boxShadow:"-24px 0 80px rgba(0,0,0,0.1)", scrollbarWidth:"none", msOverflowStyle:"none" }}>

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4"
          style={{ background:"rgba(255,253,248,0.95)", backdropFilter:"blur(20px)", borderBottom:`1px solid rgba(239,229,216,0.5)` }}>
          <p className="text-[13px] font-bold text-[#1F1F1F]">User Profile</p>
          <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
            className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
            style={{ background:"rgba(239,229,216,0.5)", color:"#888" }}>
            <FiX size={15}/>
          </motion.button>
        </div>

        {/* Hero */}
        <div className="px-6 pt-5 pb-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-44 h-44 pointer-events-none"
            style={{ background:"radial-gradient(circle,rgba(107,138,90,0.12),transparent)", filter:"blur(40px)" }}/>
          <div className="flex gap-4 items-start relative">
            <UserAvatar user={user} size={72} radius={20}/>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="font-black text-[17px] text-[#1F1F1F]"
                  style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>{user.name}</h2>
                <TierPill tier={user.tier}/>
              </div>
              <p className="text-[12px] mb-2.5" style={{ color:T.muted }}>{user.email}</p>
              <div className="flex flex-wrap gap-2">
                <StatusDot status={user.status}/>
                <span className="text-[11px] font-semibold text-zinc-500 flex items-center gap-1">
                  <FiGlobe size={12} className="text-zinc-400" /> {loc.city}, {loc.country}
                </span>
                <span className="text-[11px] font-semibold text-zinc-500 flex items-center gap-1">
                  <FiClock size={12} className="text-[#6B8A5A]" /> Active: {user.lastLogin}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Wellness ring */}
        <div className="px-6 py-4 border-t" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="grid grid-cols-[100px_1fr] gap-4 items-center">
            <div style={{ position:"relative", width:100, height:100 }}>
              <svg viewBox="0 0 120 120" style={{ position:"absolute", inset:0, width:"100%", height:"100%", transform:"rotate(-90deg)" }}>
                <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(239,229,216,0.6)" strokeWidth="12"/>
                <motion.circle cx="60" cy="60" r={R} fill="none" stroke="url(#wg)" strokeWidth="12" strokeLinecap="round"
                  strokeDasharray={C} initial={{ strokeDashoffset:C }}
                  animate={{ strokeDashoffset:C-C*(detailsStats.wellness/100) }}
                  transition={{ duration:1.2, delay:0.3, ease:"easeOut" }}/>
                <defs><linearGradient id="wg" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={T.olive}/><stop offset="100%" stopColor={T.olive2}/>
                </linearGradient></defs>
              </svg>
              <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent: "center" }}>
                <span className="font-black text-[#1F1F1F]"
                  style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-1px" }}>{detailsStats.wellness}</span>
                <span className="text-[9px] font-bold uppercase tracking-[0.6px]" style={{ color:T.light }}>SCORE</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                [detailsStats.sessions,"Sessions","rgba(255,244,232,0.7)",T.orange],
                [`${detailsStats.streak}d`,"Streak","rgba(255,244,232,0.7)",T.orange],
                [`${detailsStats.progress}%`,"Progress","rgba(245,250,242,0.7)",T.olive],
                [`${detailsStats.hours}h`,"Hours","rgba(245,250,242,0.7)",T.olive],
              ].map(([v,l,bg,c]) => (
                <div key={l} className="rounded-[12px] p-2.5" style={{ background:bg }}>
                  <p className="font-black" style={{ fontFamily:"'Poppins',sans-serif", fontSize:18, color:c, letterSpacing:"-0.5px" }}>{v}</p>
                  <p className="text-[10px] font-bold uppercase tracking-[0.6px] mt-0.5" style={{ color:T.muted }}>{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Current Subscription Plan Card */}
        <div className="px-6 py-4 border-t bg-amber-50/20 text-left" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3 text-amber-900 flex items-center gap-1.5">
            <FiCreditCard size={12} className="text-amber-600" /> Current Subscription Plan
          </p>
          <div className="rounded-[16px] p-3.5 border transition-all"
            style={{ background:"rgba(255,255,255,0.9)", borderColor:"rgba(242,154,12,0.25)", boxShadow:"0 4px 16px rgba(242,154,12,0.06)" }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="text-[14px] font-black text-zinc-900 block" style={{ fontFamily:"'Poppins',sans-serif" }}>
                  {details?.currentPlan || user.planName || user.tier || "Free Trial"}
                </span>
                <span className="text-[11px] font-bold text-zinc-400">
                  {details?.currentTier ? `Tier: ${details.currentTier}` : (user.hasActiveSubscription ? "Active Membership" : "Free Trial Account")}
                </span>
              </div>
              <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${
                user.isExpired || details?.isSubscriptionExpired || details?.subscriptionStatus === "Expired"
                  ? "bg-red-50 text-red-700 border-red-200"
                  : (details?.currentPlanDaysLeft > 0 || user.hasActiveSubscription) && user.tier !== "Free Trial"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-zinc-100 text-zinc-600 border-zinc-200"
              }`}>
                {user.isExpired || details?.isSubscriptionExpired || details?.subscriptionStatus === "Expired"
                  ? "Expired Plan"
                  : (details?.currentPlanDaysLeft > 0 || user.hasActiveSubscription) && user.tier !== "Free Trial"
                  ? "Active Plan"
                  : "Free Trial"}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 pt-2.5 border-t border-zinc-100 text-[11px]">
              <div>
                <span className="text-zinc-400 text-[9px] font-bold uppercase block">Days Remaining</span>
                <span className={`font-black text-[13px] ${
                  user.isExpired || details?.isSubscriptionExpired || details?.subscriptionStatus === "Expired"
                    ? "text-red-600"
                    : "text-amber-600"
                }`}>
                  {user.isExpired || details?.isSubscriptionExpired || details?.subscriptionStatus === "Expired"
                    ? "0 Days (Expired)"
                    : (details?.currentPlanDaysLeft !== undefined ? `${details.currentPlanDaysLeft} Days` : (user.daysLeft ? `${user.daysLeft} Days` : "0 Days"))}
                </span>
              </div>
              <div>
                <span className="text-zinc-400 text-[9px] font-bold uppercase block">Plan Expiry Date</span>
                <span className="font-extrabold text-zinc-700">
                  {details?.currentPlanEndDate
                    ? new Date(details.currentPlanEndDate).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })
                    : (user.subscriptionEndDate ? new Date(user.subscriptionEndDate).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" }) : "—")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Account details — real data */}
        <div className="px-6 py-4 border-t" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Account Details</p>
          {[
            ["User ID",      `DY-${String(user.id).padStart(4,"0")}`, T.olive],
            ["Username",     user.userName,                            T.dark],
            ["Phone",        user.phone,                               T.dark],
            ["Gender",       user.gender,                              T.dark],
            ["Joined",       user.joined,                              T.dark],
            ...(loc.area && loc.area !== "N/A" ? [["Area/Street", loc.area, T.dark]] : []),
            ["Exact City",   loc.city,                                T.dark],
            ["Exact State",  loc.state,                               T.dark],
            ["Country",      loc.country,                             T.dark],
          ].map(([k,v,c]) => (
            <div key={k} className="flex justify-between items-center py-2 border-b" style={{ borderColor:"rgba(239,229,216,0.4)" }}>
              <span className="text-[12px]" style={{ color:T.muted }}>{k}</span>
              <span className="text-[13.5px] font-semibold text-right" style={{ color:c }}>{v||"—"}</span>
            </div>
          ))}
        </div>

        {/* Health & Yoga Preferences (Dynamic from User Profile) */}
        <div className="px-6 py-4 border-t bg-purple-50/30 text-left" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3 text-purple-900 flex items-center gap-1.5">
            <FiHeart size={12} className="text-purple-600" /> Health & Yoga Preferences
          </p>
          {loading ? (
            <p className="text-[11px] text-zinc-400 font-semibold py-1">Loading profile...</p>
          ) : (
            <div className="space-y-2.5">
              {details?.healthConditions && details.healthConditions !== "None" && details.healthConditions !== "none" && details.healthConditions !== "[\"None\"]" ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {details.healthConditions.replace(/[\[\]"']/g, "").split(",").map((h, i) => (
                    <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-purple-100/80 text-purple-800 border border-purple-200">
                      ⚠️ {h.trim()}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-zinc-400 font-medium">No health restrictions logged.</p>
              )}

              {details?.healthNotes && (
                <div className="p-3 rounded-xl bg-white border border-purple-100 text-[12px] text-zinc-700 leading-relaxed shadow-sm">
                  <strong className="text-purple-950 font-bold block mb-0.5">Instructor Notes:</strong>
                  {details.healthNotes}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-600 bg-white/80 p-2.5 rounded-xl border border-purple-100/60 shadow-sm">
                <div>
                  <span className="text-zinc-400 block text-[9px] uppercase font-bold">Yoga Goal</span>
                  <span className="font-extrabold text-zinc-800">{details?.yogaGoal || "General Wellness"}</span>
                </div>
                <div>
                  <span className="text-zinc-400 block text-[9px] uppercase font-bold">Experience Level</span>
                  <span className="font-extrabold text-zinc-800">{details?.yogaLevel || "Beginner"}</span>
                </div>
                {details?.yogaStyle && (
                  <div>
                    <span className="text-zinc-400 block text-[9px] uppercase font-bold">Yoga Style</span>
                    <span className="font-extrabold text-zinc-800">{details.yogaStyle}</span>
                  </div>
                )}
                {details?.dietaryPreference && (
                  <div>
                    <span className="text-zinc-400 block text-[9px] uppercase font-bold">Diet</span>
                    <span className="font-extrabold text-zinc-800">{details.dietaryPreference}</span>
                  </div>
                )}
                {details?.bloodGroup && (
                  <div>
                    <span className="text-zinc-400 block text-[9px] uppercase font-bold">Blood Group</span>
                    <span className="font-extrabold text-zinc-800">{details.bloodGroup}</span>
                  </div>
                )}
                {(details?.weight || details?.height) && (
                  <div>
                    <span className="text-zinc-400 block text-[9px] uppercase font-bold">Height / Weight</span>
                    <span className="font-extrabold text-zinc-800">{details?.height ? `${details.height} cm` : ""} {details?.weight ? `· ${details.weight} kg` : ""}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* CHECK-IN CHECK-OUT HISTORY TIMELINE UX */}
        <div className="px-6 py-5 border-t text-left" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-4" style={{ color:T.light }}>Attendance Log & Location</p>
          {loading ? (
            <p className="text-[11px] text-zinc-400 font-semibold py-2">Loading logs...</p>
          ) : !details?.attendances || details.attendances.length === 0 ? (
            <p className="text-[11px] text-zinc-400 font-semibold py-2">No attendance logs found for this user.</p>
          ) : (
            <div className="max-h-[260px] overflow-y-auto pr-1 flex flex-col pl-2">
              {details.attendances.map((a, idx) => (
                <div key={a.id || idx} className="relative pl-6 pb-5 border-l last:pb-0" style={{ borderColor: "rgba(107,138,90,0.2)" }}>
                  {/* Timeline bullet dot */}
                  <div className="absolute left-[-6px] top-1 w-3 h-3 rounded-full border-2 bg-white"
                    style={{ borderColor: a.checkOutTime ? T.olive : T.orange }} />
                  
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[12.5px] font-bold text-[#1F1F1F]">
                      {a.notes || "Yoga Practise Session"}
                    </span>
                    <span className="text-[10px] font-black text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-full">
                      {new Date(a.attendanceDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  
                  <div className="flex gap-3 text-[11px] text-zinc-500 font-semibold mt-1.5 bg-white/60 p-2.5 rounded-xl border border-zinc-100">
                    <div className="flex-1 flex flex-col gap-0.5">
                      <span className="text-zinc-400 text-[9px] uppercase tracking-wider font-bold">Check-In</span>
                      <span className="text-emerald-600 font-bold">🟢 {a.checkInTime || "—"}</span>
                    </div>
                    <div className="flex-1 flex flex-col gap-0.5">
                      <span className="text-zinc-400 text-[9px] uppercase tracking-wider font-bold">Check-Out</span>
                      <span className={a.checkOutTime ? "text-rose-600 font-bold" : "text-amber-500 font-bold"}>
                        {a.checkOutTime ? `🔴 ${a.checkOutTime}` : "⚡ Active Session"}
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col gap-0.5 text-right">
                      <span className="text-zinc-400 text-[9px] uppercase tracking-wider font-bold">Location</span>
                      <span className="text-zinc-700 font-bold block truncate" title={a.location || "Not recorded"}>{a.location || "Not recorded"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin actions */}
        <div className="px-6 py-5 border-t" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Admin Actions</p>
          <div className="flex flex-col gap-2.5">
            {/* Direct Notification */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => { onNotify && onNotify(user); }}
              className="w-full h-10 rounded-full text-[13px] font-bold flex items-center justify-center gap-2 text-white border-none cursor-pointer shadow-sm"
              style={{ background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})` }}
            >
              <FiBell size={14} /> Send Direct Notification
            </motion.button>

            {/* Real toggle — calls API */}
            <motion.button
              whileHover={{ background: user.isActive ? "rgba(242,154,12,0.14)" : "rgba(34,197,94,0.12)" }}
              onClick={handleToggle}
              disabled={toggling}
              className="w-full h-10 rounded-full text-[13px] font-black flex items-center justify-center gap-2"
              style={{
                background: user.isActive ? "rgba(242,154,12,0.08)" : "rgba(34,197,94,0.08)",
                color:      user.isActive ? T.orange2 : T.greenDk,
                border:    `1px solid ${user.isActive ? "rgba(242,154,12,0.2)" : "rgba(34,197,94,0.2)"}`,
                cursor:"pointer", fontFamily:"'Inter',sans-serif", transition:"background 0.2s",
                opacity: toggling ? 0.6 : 1,
              }}>
              <FiBox size={13}/> {toggling ? "Updating…" : user.isActive ? "Deactivate Account" : "Activate Account"}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Custom Luxury Delete Confirmation Dialog */}
      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !deleting && setShowDeleteModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="relative w-full max-w-md bg-white rounded-[28px] border border-rose-200 shadow-2xl p-6 sm:p-7 z-10 text-left"
            >
              <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 mb-4 shadow-sm">
                <FiTrash2 size={24} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#111111] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Delete User Account?
              </h3>
              <p className="text-xs sm:text-sm text-[#666666] leading-relaxed mb-6 font-medium">
                Are you sure you want to permanently delete the account for <strong className="text-[#111111]">{user.name}</strong> ({user.email})? All registrations, attendance records, and profile history will be erased.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => setShowDeleteModal(false)}
                  className="px-5 py-2.5 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={handleConfirmDelete}
                  className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all shadow-md hover:shadow-lg cursor-pointer flex items-center gap-2 disabled:opacity-50"
                >
                  {deleting ? (
                    <>
                      <FiRefreshCw className="animate-spin" size={12} />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={12} />
                      <span>Delete Account</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── ADD USER MODAL (WITH ROBUST VALIDATION & UPWARD GENDER DROPDOWN) ─────────
function AddUserModal({ onClose, onCreated, showToast }) {
  const [form, setForm] = useState({
    userName: "",
    fullName: "",
    email: "",
    phone: "+91 ",
    password: "",
    gender: ""
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [checkingUnique, setCheckingUnique] = useState({ email: false, phone: false, userName: false });
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const checkTimerRef = useRef({});

  const checkUniqueness = useCallback((field, value) => {
    if (!value) return;
    const v = value.trim();
    if (field === "email" && !/^[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/.test(v)) return;
    if (field === "phone" && !/^\+[0-9]{2}\s?[0-9]{10}$/.test(v)) return;
    if (field === "userName" && !/^[a-zA-Z0-9_.-]{3,30}$/.test(v)) return;

    if (checkTimerRef.current[field]) {
      clearTimeout(checkTimerRef.current[field]);
    }

    checkTimerRef.current[field] = setTimeout(async () => {
      setCheckingUnique(prev => ({ ...prev, [field]: true }));
      try {
        const query = { [field]: v };
        const res = await adminApi.checkUserUniqueness(query);
        const data = res?.data || {};

        if (field === "email" && data.emailExists) {
          setErrors(prev => ({ ...prev, email: "This email address is already registered" }));
        } else if (field === "phone" && data.phoneExists) {
          setErrors(prev => ({ ...prev, phone: "This mobile number is already registered" }));
        } else if (field === "userName" && data.usernameExists) {
          setErrors(prev => ({ ...prev, userName: "This username is already taken" }));
        }
      } catch (err) {
        console.warn("Uniqueness check error:", err);
      } finally {
        setCheckingUnique(prev => ({ ...prev, [field]: false }));
      }
    }, 280);
  }, []);

  const validateField = (field, value) => {
    let err = "";
    const v = (value || "").trim();

    if (field === "fullName") {
      if (!v) {
        err = "Full name is required";
      } else if (!/^[a-zA-Z\s]+$/.test(v)) {
        err = "Only alphabets and spaces are allowed (no numbers or special characters)";
      } else if (v.replace(/\s+/g, "").length < 2) {
        err = "Full name must contain at least 2 alphabetic letters";
      }
    } else if (field === "userName") {
      if (!v) {
        err = "Username is required";
      } else if (!/^[a-zA-Z0-9_.-]{3,30}$/.test(v)) {
        err = "3-30 chars with letters, numbers, hyphens, dots, or underscores";
      }
    } else if (field === "email") {
      if (!v) {
        err = "Email address is required";
      } else if (!/^[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/.test(v)) {
        err = "Please enter a valid email address without unwanted special characters (e.g. priya@gmail.com)";
      }
    } else if (field === "phone") {
      const sanitized = v.trim();
      if (!sanitized || sanitized === "+") {
        err = "Mobile number with country code is required";
      } else if (!/^\+[0-9]{2}\s?[0-9]{10}$/.test(sanitized)) {
        err = "Must start with '+' and 2-digit country code, space, and 10-digit mobile number (e.g. +91 9876543210)";
      }
    } else if (field === "password") {
      if (!value) {
        err = "Password is required";
      } else if (value.length < 6) {
        err = "Password must be at least 6 characters";
      } else if (!/(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&#^-_.,])/.test(value)) {
        err = "Must contain letters, numbers & special character (@$!%*?&#^-_.,)";
      }
    } else if (field === "gender") {
      if (!v) {
        err = "Please select a gender";
      }
    }
    return err;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const formatErr = validateField(field, form[field]);
    setErrors(prev => ({ ...prev, [field]: formatErr }));

    if (!formatErr && (field === "email" || field === "phone" || field === "userName")) {
      checkUniqueness(field, form[field]);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    const formatErr = validateField(field, value);

    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: formatErr }));
    }

    if (!formatErr && (field === "email" || field === "phone" || field === "userName")) {
      checkUniqueness(field, value);
    }
  };

  const validateAll = () => {
    const newErrors = {};
    Object.keys(form).forEach(k => {
      const err = validateField(k, form[k]);
      if (err) newErrors[k] = err;
    });
    setErrors(newErrors);
    setTouched({
      userName: true,
      fullName: true,
      email: true,
      phone: true,
      password: true,
      gender: true
    });
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateAll()) {
      showToast("Please fix the validation errors before submitting.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        phone: form.phone.replace(/\s+/g, "").trim(),
        email: form.email.trim().toLowerCase(),
        fullName: form.fullName.trim(),
        userName: form.userName.trim(),
      };
      const res = await adminApi.createUser(payload);
      if (res?.success || res?.data?.success) {
        showToast(`✓ User "${form.fullName}" created successfully!`);
        onCreated();
        onClose();
      } else {
        const errorMsg = res?.message || res?.data?.message || "Failed to create user";
        showToast(errorMsg);
      }
    } catch (e) {
      showToast(e.message || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const getInpStyle = (fieldName) => {
    const hasErr = touched[fieldName] && errors[fieldName];
    const isVal = touched[fieldName] && !errors[fieldName] && form[fieldName];
    return {
      width: "100%",
      padding: "10px 14px",
      borderRadius: 13,
      border: hasErr ? "1.5px solid #EF4444" : isVal ? "1.5px solid #22C55E" : "1px solid rgba(239,229,216,0.9)",
      background: hasErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
      fontFamily: "'Inter',sans-serif",
      fontSize: 13,
      color: "#1F1F1F",
      outline: "none",
      boxSizing: "border-box",
      transition: "all 0.2s",
    };
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 z-[200]" style={{ background: "rgba(15,15,15,0.38)", backdropFilter: "blur(6px)" }} />
      <motion.div initial={{ scale: 0.93, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.93, opacity: 0, y: 20 }} transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
        className="ad-modal-wrap fixed inset-0 z-[201] flex items-center justify-center p-4 sm:p-6"
        onClick={e => e.stopPropagation()}>
        <div className="ad-pad w-full max-w-2xl lg:max-w-3xl rounded-[32px] p-7 sm:p-9"
          style={{
            ...glass,
            boxShadow: "0 40px 100px rgba(0,0,0,0.24)",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}>
          
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-black text-[18px] text-[#1F1F1F]"
                  style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>Add New User</h2>
                <span className="text-[10px] font-black text-emerald-800 bg-emerald-100/90 px-2 py-0.5 rounded-full border border-emerald-300">
                  ADMIN DIRECT
                </span>
              </div>
              <p className="text-[12px] mt-0.5 text-zinc-400">Create user account with instant credentials</p>
            </div>
            <motion.button whileHover={{ background: "rgba(239,229,216,0.9)" }} onClick={onClose}
              className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
              style={{ background: "rgba(239,229,216,0.5)", color: "#888" }}>
              <FiX size={15} />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {/* Row 1: Full Name & Username */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Full Name */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-500">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] text-zinc-400 font-medium">Alphabets & spaces only</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="e.g. Priya Sharma"
                    value={form.fullName}
                    onChange={e => handleChange("fullName", e.target.value)}
                    onBlur={() => handleBlur("fullName")}
                    style={getInpStyle("fullName")}
                  />
                  {touched.fullName && !errors.fullName && form.fullName && (
                    <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                  )}
                </div>
                {touched.fullName && errors.fullName && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.fullName}
                  </p>
                )}
              </div>

              {/* Username */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-500">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] text-zinc-400 font-medium">Letters, numbers, - _ .</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="e.g. priya_sharma"
                    value={form.userName}
                    onChange={e => handleChange("userName", e.target.value)}
                    onBlur={() => handleBlur("userName")}
                    style={getInpStyle("userName")}
                  />
                  {checkingUnique.userName ? (
                    <FiRefreshCw className="animate-spin absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={13} />
                  ) : touched.userName && !errors.userName && form.userName ? (
                    <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                  ) : null}
                </div>
                {touched.userName && errors.userName && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.userName}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: Email & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Email */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-500">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] text-zinc-400 font-medium">Valid email</span>
                </div>
                <div className="relative">
                  <input
                    type="email"
                    placeholder="e.g. priya@gmail.com"
                    value={form.email}
                    onChange={e => handleChange("email", e.target.value)}
                    onBlur={() => handleBlur("email")}
                    style={getInpStyle("email")}
                  />
                  {checkingUnique.email ? (
                    <FiRefreshCw className="animate-spin absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={13} />
                  ) : touched.email && !errors.email && form.email ? (
                    <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                  ) : null}
                </div>
                {touched.email && errors.email && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-500">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] text-zinc-400 font-medium">+CC 10-Digits (e.g. +91 9876543210)</span>
                </div>
                <div className="relative">
                  <input
                    type="tel"
                    placeholder="+91 9876543210"
                    value={form.phone}
                    onChange={e => handleChange("phone", e.target.value)}
                    onBlur={() => handleBlur("phone")}
                    style={getInpStyle("phone")}
                  />
                  {checkingUnique.phone ? (
                    <FiRefreshCw className="animate-spin absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={13} />
                  ) : touched.phone && !errors.phone && form.phone && form.phone !== "+91 " && form.phone !== "+91" ? (
                    <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                  ) : null}
                </div>
                {touched.phone && errors.phone && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Row 3: Password & Gender */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-500">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] text-zinc-400 font-medium">Letters, digits, symbols</span>
                </div>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="Min 6 chars (Yoga@2026)"
                    value={form.password}
                    onChange={e => handleChange("password", e.target.value)}
                    onBlur={() => handleBlur("password")}
                    style={getInpStyle("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 text-[11px] font-bold border-none bg-transparent cursor-pointer"
                  >
                    {showPass ? "Hide" : "Show"}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.password}
                  </p>
                )}
              </div>

              {/* Gender Dropdown — Upward Popout */}
              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-500">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <span className="text-[10px] text-zinc-400 font-medium">Required</span>
                </div>
                <CustomDropdown
                  label="Select gender"
                  value={form.gender}
                  onChange={v => {
                    handleChange("gender", v);
                    setTouched(prev => ({ ...prev, gender: true }));
                  }}
                  options={[
                    { value: "", label: "Select gender" },
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                    { value: "Other", label: "Other" }
                  ]}
                  icon={FiUser}
                  fullWidth={true}
                  direction="up"
                />
                {touched.gender && errors.gender && (
                  <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                    <FiAlertTriangle size={11} className="shrink-0" /> {errors.gender}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-2 pt-3 border-t border-zinc-200/60">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-full text-[13px] font-semibold transition-all hover:bg-zinc-100"
                style={{ background: "rgba(255,255,255,0.9)", border: "1.5px solid rgba(239,229,216,0.9)", color: "#555", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                disabled={saving}
                className="flex-1 h-11 rounded-full text-white text-[13px] font-bold flex items-center justify-center gap-2 shadow-md border-none cursor-pointer"
                style={{
                  background: `linear-gradient(135deg,#6B8A5A,#8BA47A)`,
                  boxShadow: `0 6px 18px rgba(107,138,90,0.38)`,
                  fontFamily: "'Inter',sans-serif",
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? (
                  <>
                    <FiRefreshCw className="animate-spin" size={14} /> Creating…
                  </>
                ) : (
                  <>
                    <FiUserPlus size={14} /> Create User
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </>
  );
}

// ─── DIRECT NOTIFICATION MODAL (WITH TITLE & MESSAGE VALIDATION) ───────────
function DirectNotificationModal({ user, onClose, showToast }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);

  const firstName = (user?.name || "Member").split(" ")[0];

  const TEMPLATES = [
    { label: "Class Reminder", title: "Live Yoga Class Starting Soon", message: `Namaste ${firstName}, your live session is scheduled to start shortly. Join early to warm up!` },
    { label: "Plan Expiry", title: "Subscription Notice - Action Required", message: `Dear ${firstName}, your Divyyog plan is expiring soon. Renew today to keep your daily practice uninterrupted!` },
    { label: "Progress Cheer", title: "Great Progress - Keep It Up", message: `Congratulations ${firstName}! You've achieved impressive consistency with ${user?.sessions || 0} practice sessions.` },
    { label: "Diet Plan Ready", title: "Your Personalized Diet Plan is Live", message: `Hello ${firstName}, your updated nutrition and wellness plan has been tailored by your instructor.` },
  ];

  const validateTitle = (val) => {
    const v = (val || "").trim();
    if (!v) return "Notification title is required";
    if (v.length < 3) return "Title must be at least 3 characters";
    if (!/^[a-zA-Z0-9\s-_.,!?()'"&@:;#+*\/]+$/.test(v)) {
      return "Title can only contain letters, numbers, and allowed punctuation (- _ . , ! ?)";
    }
    return "";
  };

  const validateMessage = (val) => {
    const v = (val || "").trim();
    if (!v) return "Message content is required";
    if (v.length < 5) return "Message must be at least 5 characters";
    return "";
  };

  const handleTitleChange = (val) => {
    setTitle(val);
    if (touched.title) {
      setErrors(prev => ({ ...prev, title: validateTitle(val) }));
    }
  };

  const handleMessageChange = (val) => {
    setMessage(val);
    if (touched.message) {
      setErrors(prev => ({ ...prev, message: validateMessage(val) }));
    }
  };

  const handleSend = async () => {
    const tErr = validateTitle(title);
    const mErr = validateMessage(message);
    setErrors({ title: tErr, message: mErr });
    setTouched({ title: true, message: true });

    if (tErr || mErr) {
      showToast("Please provide valid title and message before sending.");
      return;
    }

    setSending(true);
    try {
      const res = await adminApi.sendNotification({
        userId: user.id,
        title: title.trim(),
        message: message.trim(),
        audience: user.email || user.userName || `User ID: ${user.id}`,
      });
      if (res.success || res.data?.success) {
        showToast(`✓ Notification sent directly to ${user.name}!`);
        onClose();
      } else {
        showToast(res.message || res.data?.message || "Failed to send notification.");
      }
    } catch (err) {
      showToast(err.message || "Failed to send notification.");
    } finally {
      setSending(false);
    }
  };

  const getInpStyle = (hasErr) => ({
    width: "100%",
    padding: "10px 14px",
    borderRadius: 13,
    background: hasErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
    border: hasErr ? "1.5px solid #EF4444" : "1px solid rgba(239,229,216,0.9)",
    fontFamily: "'Inter',sans-serif",
    fontSize: 13,
    color: "#1F1F1F",
    outline: "none",
    boxSizing: "border-box",
    transition: "all 0.2s",
  });

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 z-[200]" style={{ background: "rgba(15,15,15,0.38)", backdropFilter: "blur(6px)" }} />
      <motion.div initial={{ scale: 0.93, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.93, opacity: 0, y: 20 }} transition={{ duration: 0.28, ease: [0.25, 1, 0.5, 1] }}
        className="ad-modal-wrap fixed inset-0 z-[201] flex items-center justify-center p-4 sm:p-6"
        onClick={e => e.stopPropagation()}>
        <div className="ad-pad w-full max-w-xl sm:max-w-2xl rounded-[32px] p-7 sm:p-8 text-left"
          style={{ ...glass, boxShadow: "0 40px 100px rgba(0,0,0,0.24)" }}>
          
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center text-amber-600 flex-shrink-0">
                <FiBell size={18} />
              </div>
              <div>
                <h2 className="font-black text-[18px] text-[#1F1F1F]"
                  style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.4px" }}>
                  Direct Notification
                </h2>
                <p className="text-[12px] text-zinc-400">Send an instant validated alert directly to this member</p>
              </div>
            </div>
            <motion.button whileHover={{ background: "rgba(239,229,216,0.9)" }} onClick={onClose}
              className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
              style={{ background: "rgba(239,229,216,0.5)", color: "#888" }}>
              <FiX size={15} />
            </motion.button>
          </div>

          {/* Recipient Card */}
          <div className="flex items-center justify-between p-3 rounded-2xl bg-[#FAF6EE] border border-[rgba(239,229,216,0.8)] mb-4">
            <div className="flex items-center gap-2.5">
              <UserAvatar user={user} size={36} radius={12} />
              <div>
                <p className="text-xs font-black text-zinc-900">{user.name}</p>
                <p className="text-[11px] text-zinc-400">{user.email || `@${user.userName}`}</p>
              </div>
            </div>
            <TierPill tier={user.tier} daysLeft={user.daysLeft} />
          </div>

          {/* Quick Templates */}
          <div className="mb-4">
            <label className="text-[10px] font-bold uppercase tracking-[0.6px] mb-1.5 block text-zinc-400">Quick Templates</label>
            <div className="flex flex-wrap gap-1.5">
              {TEMPLATES.map((tmpl, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    handleTitleChange(tmpl.title);
                    handleMessageChange(tmpl.message);
                    setTouched({ title: true, message: true });
                  }}
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/90 hover:bg-[#6B8A5A]/10 hover:text-[#6B8A5A] hover:border-[#6B8A5A]/30 border border-zinc-200 text-zinc-600 transition-all cursor-pointer"
                >
                  {tmpl.label}
                </button>
              ))}
            </div>
          </div>

          {/* Inputs */}
          <div className="flex flex-col gap-3 mb-6">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-400">
                  Notification Title <span className="text-red-500">*</span>
                </label>
                <span className="text-[10px] text-zinc-400 font-medium">Letters, numbers & punctuation</span>
              </div>
              <input
                type="text"
                placeholder="e.g. Special Session Invitation"
                value={title}
                onChange={e => handleTitleChange(e.target.value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, title: true }));
                  setErrors(prev => ({ ...prev, title: validateTitle(title) }));
                }}
                style={getInpStyle(touched.title && errors.title)}
              />
              {touched.title && errors.title && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.title}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-bold uppercase tracking-[0.6px] text-zinc-400">
                  Message Content <span className="text-red-500">*</span>
                </label>
                <span className="text-[10px] text-zinc-400 font-medium">Min 5 characters</span>
              </div>
              <textarea
                rows={4}
                placeholder="Type your direct message here..."
                value={message}
                onChange={e => handleMessageChange(e.target.value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, message: true }));
                  setErrors(prev => ({ ...prev, message: validateMessage(message) }));
                }}
                style={{ ...getInpStyle(touched.message && errors.message), resize: "none" }}
              />
              {touched.message && errors.message && (
                <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1">
                  <FiAlertTriangle size={11} className="shrink-0" /> {errors.message}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={handleSend} disabled={sending}
              className="flex-1 py-3 rounded-[14px] text-white font-bold text-[13px] border-none cursor-pointer disabled:opacity-50 transition-all shadow-md flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg,#6B8A5A,#8BA47A)" }}>
              {sending ? <FiRefreshCw className="animate-spin" size={14} /> : <FiBell size={14} />}
              {sending ? "Sending..." : "Send Notification"}
            </motion.button>
            <motion.button whileHover={{ background: "rgba(239,229,216,0.7)" }} onClick={onClose}
              className="px-5 py-3 rounded-[14px] font-semibold text-[13px] border cursor-pointer bg-white hover:bg-zinc-50"
              style={{ borderColor: "rgba(239,229,216,0.9)", color: "#555" }}>
              Cancel
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function UserManagementPage() {
  // ── Data state
  const [users,       setUsers]       = useState([]);
  const [totalUsers,  setTotalUsers]  = useState(0);
  const [statsData,   setStatsData]   = useState(null);
  const [loading,     setLoading]     = useState(true);

  // ── Filter / UI state
  const [search,       setSearch]       = useState("");
  const [debouncedQ,   setDebouncedQ]   = useState("");
  const [tierFilter,   setTierFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [countryFilter,setCountryFilter]= useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [view,         setView]         = useState("table");
  const [sortCol,      setSortCol]      = useState("name");
  const [sortDir,      setSortDir]      = useState(1);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize,     setPageSize]     = useState(10);
  const [selected,     setSelected]     = useState(new Set());
  const [drawerUser,   setDrawerUser]   = useState(null);
  const [notifyUser,   setNotifyUser]   = useState(null);
  const [showAdd,      setShowAdd]      = useState(false);
  const [toast,        setToast]        = useState(null);
  const [menuUserId,   setMenuUserId]   = useState(null);
  const [menuPos,      setMenuPos]      = useState(null); // fixed screen position of the table row "⋯" menu
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);

  // Table row "⋯" menu: anchored to the button on screen (rendered in a portal so the
  // table's overflow never clips it) and flipped upward when there isn't room below.
  const openRowMenu = (e, userId) => {
    if (menuUserId === userId) { setMenuUserId(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const MENU_H = 196, GAP = 6, EDGE = 12;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < MENU_H + EDGE && r.top > spaceBelow;
    setMenuPos({
      right: Math.max(EDGE, window.innerWidth - r.right),
      top: openUp ? undefined : r.bottom + GAP,
      bottom: openUp ? window.innerHeight - r.top + GAP : undefined,
    });
    setMenuUserId(userId);
  };

  useEffect(() => {
    if (view !== "table" || menuUserId == null || !menuPos) return;
    const close = () => setMenuUserId(null);
    const onDown = (e) => { if (!e.target.closest?.("[data-row-menu]")) close(); };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [view, menuUserId, menuPos]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    country: true,
    tier: true,
    status: true,
    sessions: true,
    streak: true,
    joined: true,
    lastLogin: true
  });

  // ── Responsive card column detector (1..4 columns)
  const [cardCols, setCardCols] = useState(() => {
    if (typeof window === "undefined") return 3;
    if (window.innerWidth >= 1600) return 4;
    if (window.innerWidth >= 1100) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1600) setCardCols(4);
      else if (window.innerWidth >= 1100) setCardCols(3);
      else if (window.innerWidth >= 640) setCardCols(2);
      else setCardCols(1);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Dynamic pagination: Always display exactly 3 rows of cards in card view, or user-selected pageSize in table view
  const dynamicPageSize = useMemo(() => {
    if (view === "card") {
      return cardCols * 3; // 3 complete rows of cards for current screen resolution
    }
    if (pageSize === "all") {
      return totalUsers > 0 ? totalUsers : 50;
    }
    return Number(pageSize);
  }, [view, cardCols, pageSize, totalUsers]);

  const pageSizeOptions = useMemo(() => [
    { label: "Show 5", value: 5 },
    { label: "Show 10", value: 10 },
    { label: "Show 20", value: 20 },
    { label: "Show 50", value: 50 },
    { label: `Show All (${totalUsers || 0})`, value: "all" },
  ], [totalUsers]);

  const showToast = useCallback((msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); }, []);

  // ── Debounce search → triggers re-fetch
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(search); setCurrentPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Fetch paginated users from API with dynamic page size
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const apiStatus = statusFilter === "all" ? "" : statusFilter.toLowerCase();
      const res = await adminApi.getUsers({
        search:   debouncedQ,
        status:   apiStatus,
        page:     currentPage,
        pageSize: dynamicPageSize,
      });
      const raw   = res.data?.users ?? res.data?.Users ?? [];
      const total = res.data?.total ?? res.data?.Total ?? 0;
      setUsers(raw.map(mapUser));
      setTotalUsers(total);
    } catch (e) {
      showToast(`Failed to load users: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, statusFilter, currentPage, dynamicPageSize, showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Fetch stats for stat strip
  useEffect(() => {
    adminApi.getDashboardStats()
      .then(r => setStatsData(normStats(r.data || {})))
      .catch(() => {});
  }, []);

  // ── Parse ?userId=X from query params to auto-open profile drawer
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryUserId = params.get("userId");
    if (!queryUserId) return;

    if (users.length > 0) {
      const match = users.find(u => String(u.id) === String(queryUserId));
      if (match) {
        setDrawerUser(match);
      } else {
        // Fallback: Fetch directly from API if not found in current page
        adminApi.getUser(parseInt(queryUserId))
          .then(res => {
            if (res.success) {
              setDrawerUser(mapUser(res.data, 0));
            }
          })
          .catch(() => {});
      }
    }
  }, [users]);

  // ── Handle toggle status in-place (optimistic UI)
  const handleStatusToggled = useCallback((userId) => {
    setUsers(prev => prev.map(u =>
      u.id === userId
        ? { ...u, isActive:!u.isActive, status:u.isActive?"Inactive":"Active" }
        : u
    ));
    // Also update drawer user if open
    setDrawerUser(prev => prev?.id === userId
      ? { ...prev, isActive:!prev.isActive, status:prev.isActive?"Inactive":"Active" }
      : prev
    );
  }, []);

  const { tiers: dynamicTiers } = useDynamicPackages();

  // ── Normalize tier alias helper (matches "Standard" with "Standard Plan", "Free" with "Free Trial", etc.)
  const normalizeTierKey = (val) => {
    if (!val) return "free";
    const s = val.toLowerCase().replace(/plan|tier/g, "").trim();
    if (s.includes("free") || s.includes("trial")) return "free";
    if (s.includes("ytt") || s.includes("pro")) return "pro";
    if (s.includes("prem")) return "premium";
    if (s.includes("stand")) return "standard";
    if (s.includes("basic")) return "basic";
    return s;
  };

  // ── Client-side tier + country + gender sort on current page
  const filtered = useMemo(() => {
    let data = users;
    if (tierFilter !== "all") {
      const targetKey = normalizeTierKey(tierFilter);
      const f = tierFilter.toLowerCase().trim();
      data = data.filter(u => {
        const uTierKey = normalizeTierKey(u.tier);
        const uPlanKey = normalizeTierKey(u.planName);
        const uTier = (u.tier || "").toLowerCase().trim();
        const uPlan = (u.planName || "").toLowerCase().trim();
        return uTierKey === targetKey || uPlanKey === targetKey || uTier === f || uPlan === f || uTier.includes(f) || uPlan.includes(f);
      });
    }
    if (countryFilter !== "all") data = data.filter(u => u.country === countryFilter);
    if (genderFilter !== "all")  data = data.filter(u => u.gender && u.gender.toLowerCase() === genderFilter.toLowerCase());
    return [...data].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol];
      return (typeof va === "string" ? va.localeCompare(vb) : (va||0)-(vb||0)) * sortDir;
    });
  }, [users, tierFilter, countryFilter, genderFilter, sortCol, sortDir]);

  const countriesList = useMemo(() => {
    const list = new Set(users.map(u => u.country).filter(Boolean));
    return ["all", ...Array.from(list)];
  }, [users]);

  // Clean, deduplicated dynamic tier options directly matching Live Sessions & Content Manager
  const tierOptions = useMemo(() => [
    { value: "all", label: "All Tiers" },
    ...(dynamicTiers || []).map(t => ({
      value: t,
      label: t === "Free" ? "Free Trial" : t,
    }))
  ], [dynamicTiers]);

  const statusOptions = useMemo(() => [
    { value: "all", label: "All Status", dotColor: "#888" },
    { value: "Active", label: "Active", dotColor: STATUS_COLORS.Active },
    { value: "Expired", label: "Subscription Expired", dotColor: STATUS_COLORS.Expired },
    { value: "Inactive", label: "Deactivated (Admin)", dotColor: STATUS_COLORS.Inactive },
  ], []);

  const countryOptions = useMemo(() => {
    return countriesList.map(c => ({
      value: c,
      label: c === "all" ? "All Countries" : c,
    }));
  }, [countriesList]);

  const genderOptions = useMemo(() => [
    { value: "all", label: "All Genders" },
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ], []);

  const totalPages = Math.max(1, Math.ceil(totalUsers / dynamicPageSize));

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d * -1);
    else { setSortCol(col); setSortDir(1); }
  };
  const toggleSelect = (id, checked) => setSelected(prev => { const n=new Set(prev); checked?n.add(id):n.delete(id); return n; });
  const toggleAll    = (checked)      => setSelected(checked ? new Set(filtered.map(u=>u.id)) : new Set());

  const handleExportCSV = (selectedOnly = false) => {
    const targetUsers = selectedOnly && selected.size > 0 
      ? filtered.filter(u => selected.has(u.id))
      : filtered;
    
    if (targetUsers.length === 0) {
      showToast("No users to export");
      return;
    }

    const headers = ["User ID", "Full Name", "Username", "Email", "Phone", "Country", "Tier", "Status", "Sessions", "Streak", "Joined Date"];
    const rows = targetUsers.map(u => [
      `DY-${String(u.id).padStart(4, "0")}`,
      `"${(u.fullName || u.name || "").replace(/"/g, '""')}"`,
      `"${(u.userName || "").replace(/"/g, '""')}"`,
      `"${(u.email || "").replace(/"/g, '""')}"`,
      `"${(u.phone || "").replace(/"/g, '""')}"`,
      `"${(u.country || "").replace(/"/g, '""')}"`,
      `"${(u.tier || "").replace(/"/g, '""')}"`,
      `"${(u.status || "").replace(/"/g, '""')}"`,
      u.sessions || 0,
      u.streak || 0,
      `"${(u.joined || "").replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `divyayoga_users_${selectedOnly ? "selected" : "all"}_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast(`✓ Exported ${targetUsers.length} users to CSV`);
  };

  const handleBatchToggleStatus = async (activate) => {
    if (selected.size === 0) return;
    const userIds = Array.from(selected);
    try {
      await Promise.all(userIds.map(id => adminApi.toggleUserStatus(id)));
      setUsers(prev => prev.map(u => userIds.includes(u.id) ? { ...u, isActive: activate, status: activate ? "Active" : "Inactive" } : u));
      showToast(`✓ Updated ${userIds.length} users to ${activate ? "Active" : "Inactive"}`);
      setSelected(new Set());
    } catch (err) {
      showToast("Error updating batch user status");
    }
  };

  // ── Derived display values from real stats
  const ph               = loading ? "…" : "0";
  const totalDisp        = statsData ? (Number(statsData.totalUsers) || 0).toLocaleString() : ph;
  const activeDisp       = statsData ? (Number(statsData.activeUsers) || 0).toLocaleString() : ph;
  const expiredDisp      = statsData ? (Number(statsData.expiredUsers) || 0).toLocaleString() : ph;
  const paidDisp         = statsData ? (Number(statsData.activeSubscriptions) || 0).toLocaleString() : ph;
  const inactive14dDisp  = statsData ? (Number(statsData.inactive14dUsers ?? statsData.inactiveUsers) || 0).toLocaleString() : ph;
  const deactivatedDisp  = statsData ? (Number(statsData.deactivatedUsers) || 0).toLocaleString() : ph;
  const newMonthDisp     = statsData?.newUsersThisMonth ? `+${statsData.newUsersThisMonth} new this month` : "";

  const TH = ({ col, label }) => (
    <th onClick={() => handleSort(col)}
      className="text-[10px] font-black uppercase tracking-[0.9px] text-left cursor-pointer select-none whitespace-nowrap transition-colors py-2.5 px-3.5"
      style={{ color:sortCol===col?T.olive:T.light, borderBottom:`1px solid rgba(239,229,216,0.6)` }}>
      {label} {sortCol===col?(sortDir===1?"↑":"↓"):""}
    </th>
  );

  return (
    <AdminDashboardLayout activeNav="users">
      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Ambient orb */}
        <motion.div animate={{ y:[0,-20,0], x:[0,14,0] }} transition={{ duration:14, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", top:-160, right:-80, zIndex:0 }}/>

        <div className="relative z-10 w-full min-w-0 text-[#1F1F1F]">

          {/* Topbar */}
          <motion.div initial={{ opacity:0, y:-16 }} animate={{ opacity:1, y:0 }}
            className="ad-toolbar flex items-center justify-between mb-6">
            <div>
              <h1 className="font-black text-[#1F1F1F]"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, letterSpacing:"-1px" }}>
                User Management
              </h1>
              <p className="text-[12px] mt-1" style={{ color:T.muted }}>
                {loading ? "Loading…" : `${totalDisp} registered · ${activeDisp} active · ${newMonthDisp}`}
              </p>
            </div>
            <div className="flex gap-2">
              <motion.button whileHover={{ y:-1 }} onClick={() => handleExportCSV(false)}
                className="h-9 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5"
                style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`, color:T.mid, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                <FiDownload size={13}/> Export CSV
              </motion.button>
              <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }} onClick={() => setShowAdd(true)}
                className="h-9 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-1.5"
                style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 5px 16px rgba(107,138,90,0.38)`, border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                <FiUserPlus size={13}/> Add User
              </motion.button>
            </div>
          </motion.div>

          {/* Stat strip — 6 real data cards */}
          <div className="ad-kpi grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <StatCard icon={FiUsers}         value={totalDisp}       label="Total Users"          trend={newMonthDisp||"+0 this month"}
              iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`}
              accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
              cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.04}/>
            <StatCard icon={FiUserCheck}     value={activeDisp}      label="Active Accounts"      trend="account active"
              iconBg="rgba(34,197,94,0.15)" accentColor={T.green} delay={0.08}/>
            <StatCard icon={FiCreditCard}    value={paidDisp}        label="Paid Users"           trend="unique active paid"
              iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`}
              accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`}
              cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.12}/>
            <StatCard icon={FiClock}         value={expiredDisp}     label="Expired Subscriptions" trend="plan expired" trendUp={false}
              iconBg="rgba(220,38,38,0.15)" accentColor="#DC2626"
              cardBg="linear-gradient(145deg,rgba(220,38,38,0.06),rgba(254,242,242,0.4))" delay={0.16}/>
            <StatCard icon={FiAlertTriangle} value={inactive14dDisp} label="14+ Days No Login"   trend="no login in 14+ days" trendUp={false}
              iconBg="rgba(245,158,11,0.2)" accentColor={T.amber} delay={0.20}/>
            <StatCard icon={FiUserX}         value={deactivatedDisp} label="Deactivated (Admin)"  trend="admin turned off" trendUp={false}
              iconBg="rgba(226,75,74,0.15)" accentColor={T.red}
              cardBg="linear-gradient(145deg,rgba(226,75,74,0.08),rgba(252,235,235,0.4))" delay={0.24}/>
          </div>

          {/* Filter bar */}
          <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
            className="ad-filters flex items-center gap-2.5 mb-4 flex-wrap">
            <div className="ad-filters-full flex items-center gap-2 px-4 py-2.5 rounded-[14px] flex-1 max-w-xs"
              style={{ background:"rgba(255,255,255,0.82)", border:`1px solid ${T.border}`, transition:"border-color 0.2s" }}
              onFocus={e => e.currentTarget.style.borderColor=T.olive}
              onBlur={e => e.currentTarget.style.borderColor=T.border}>
              <FiSearch size={14} style={{ color:T.light }}/>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email…"
                className="bg-transparent border-none outline-none text-[13px] text-[#1F1F1F] w-full"
                style={{ fontFamily:"'Inter',sans-serif" }}/>
            </div>
            <CustomDropdown
              label="All Tiers"
              value={tierFilter}
              onChange={(v) => { setTierFilter(v); }}
              options={tierOptions}
              icon={FiLayers}
            />
            <CustomDropdown
              label="Status"
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
              options={statusOptions}
              icon={FiFilter}
            />
            <CustomDropdown
              label="Country"
              value={countryFilter}
              onChange={setCountryFilter}
              options={countryOptions}
              icon={FiGlobe}
            />
            {view === "table" && (
              <CustomDropdown
                label="Page Size"
                value={pageSize}
                onChange={(v) => { setPageSize(v === "all" ? "all" : Number(v)); setCurrentPage(1); }}
                options={pageSizeOptions}
                icon={FiList}
              />
            )}
            <div className="ad-filters-full ml-auto flex items-center gap-3">
              <span className="text-[11px]" style={{ color:T.muted }}>
                {loading ? "Loading…" : `Showing ${filtered.length} of ${totalUsers}`}
              </span>
              <div className="flex gap-1 p-1 rounded-[12px]"
                style={{ background:"rgba(255,255,255,0.82)", border:`1px solid ${T.border}` }}>
                {[["table",FiList],["card",FiGrid]].map(([v,Icon]) => (
                  <motion.button key={v} whileTap={{ scale:0.92 }} onClick={() => { setView(v); setCurrentPage(1); }}
                    className="w-8 h-7 rounded-[8px] flex items-center justify-center border-none cursor-pointer transition-all"
                    style={view===v
                      ?{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, color:"#fff", boxShadow:`0 3px 8px rgba(107,138,90,0.3)` }
                      :{ background:"transparent", color:T.light }}>
                    <Icon size={15}/>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Main panel */}
          <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.14 }}
            className="rounded-[24px] overflow-hidden" style={glass}>

            {/* Panel header */}
            <div className="ad-wrap flex items-center justify-between gap-2 px-5 py-3.5 border-b"
              style={{ borderColor:"rgba(239,229,216,0.5)" }}>
              <div className="ad-wrap flex items-center gap-3">
                <input type="checkbox"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={e => toggleAll(e.target.checked)}
                  className="w-4 h-4 rounded-[5px] border cursor-pointer accent-[#6B8A5A]"
                  style={{ border:`1.5px solid ${T.border}` }}/>
                <span className="text-[12px] font-bold text-zinc-700">
                  {selected.size > 0 ? `${selected.size} of ${filtered.length} users selected` : "Select all users"}
                </span>
                {selected.size > 0 && (
                  <div className="ad-scroll ad-full flex items-center gap-2 ml-4">
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.96 }}
                      onClick={() => handleExportCSV(true)}
                      className="px-3 py-1 rounded-full text-[11px] font-bold bg-[#6B8A5A]/10 text-[#6B8A5A] border border-[#6B8A5A]/20 cursor-pointer">
                      Export Selected CSV ({selected.size})
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.96 }}
                      onClick={() => handleBatchToggleStatus(true)}
                      className="px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 cursor-pointer">
                      Activate Selected
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.96 }}
                      onClick={() => handleBatchToggleStatus(false)}
                      className="px-3 py-1 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-700 border border-amber-500/20 cursor-pointer">
                      Deactivate Selected
                    </motion.button>
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.96 }}
                      onClick={() => setSelected(new Set())}
                      className="px-3 py-1 rounded-full text-[11px] font-bold bg-zinc-100 text-zinc-600 border border-zinc-200 cursor-pointer">
                      Deselect All
                    </motion.button>
                  </div>
                )}
              </div>
              <div className="flex gap-2 relative">
                <motion.button whileHover={{ y:-1 }} onClick={fetchUsers}
                  className="w-8 h-8 rounded-[10px] flex items-center justify-center border cursor-pointer"
                  style={{ background:"rgba(255,255,255,0.9)", borderColor:T.border, color:"#888", transition:"all 0.2s" }}
                  title="Refresh User List"
                >
                  <FiRefreshCw size={14}/>
                </motion.button>

                {/* Column Settings Button & Dropdown */}
                <div className="relative">
                  <motion.button whileHover={{ y:-1 }} onClick={() => { setShowColumnSettings(!showColumnSettings); setShowSortOptions(false); }}
                    className="w-8 h-8 rounded-[10px] flex items-center justify-center border cursor-pointer"
                    style={{ background:showColumnSettings ? "rgba(107,138,90,0.08)" : "rgba(255,255,255,0.9)", borderColor:showColumnSettings ? T.olive : T.border, color:showColumnSettings ? T.olive : "#888", transition:"all 0.2s" }}
                    title="Toggle Columns"
                  >
                    <FiColumns size={14}/>
                  </motion.button>
                  {showColumnSettings && (
                    <div className="absolute right-0 top-10 w-48 rounded-2xl bg-white border shadow-2xl p-3 z-50 flex flex-col gap-2.5 text-left"
                      style={{ borderColor: "rgba(239,229,216,0.7)" }}>
                      <p className="text-[10px] font-black text-zinc-400 uppercase tracking-wider border-b pb-1.5 mb-1">Visible Columns</p>
                      {Object.keys(visibleColumns).map((col) => (
                        <label key={col} className="flex items-center gap-2 cursor-pointer text-xs font-bold text-zinc-600 select-none">
                          <input type="checkbox" checked={visibleColumns[col]}
                            onChange={() => setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }))}
                            className="w-3.5 h-3.5 rounded cursor-pointer accent-[#6B8A5A]"
                          />
                          {col === "lastLogin" ? "Last Login" : col.charAt(0).toUpperCase() + col.slice(1)}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sort Options Button & Dropdown */}
                <div className="relative">
                  <motion.button whileHover={{ y:-1 }} onClick={() => { setShowSortOptions(!showSortOptions); setShowColumnSettings(false); }}
                    className="w-8 h-8 rounded-[10px] flex items-center justify-center border cursor-pointer"
                    style={{ background:showSortOptions ? "rgba(107,138,90,0.08)" : "rgba(255,255,255,0.9)", borderColor:showSortOptions ? T.olive : T.border, color:showSortOptions ? T.olive : "#888", transition:"all 0.2s" }}
                    title="Sorting Options"
                  >
                    <FiArrowUp size={14} className={sortDir === -1 ? "rotate-180 transition-transform" : "transition-transform"}/>
                  </motion.button>
                  {showSortOptions && (
                    <div className="absolute right-0 top-10 w-44 rounded-2xl bg-white border shadow-2xl p-2.5 z-50 flex flex-col gap-1 text-left"
                      style={{ borderColor: "rgba(239,229,216,0.7)" }}>
                      <p className="text-[10px] font-black text-zinc-400 uppercase tracking-wider border-b pb-1.5 px-1 mb-1">Sort Columns</p>
                      {[
                        ["name", "Name"],
                        ["country", "Country"],
                        ["tier", "Plan (Tier)"],
                        ["sessions", "Sessions"],
                        ["streak", "Check-in Streak"],
                        ["joined", "Joined Date"],
                        ["lastLogin", "Last Login"]
                      ].map(([col, label]) => (
                        <button key={col} onClick={() => { handleSort(col); setShowSortOptions(false); }}
                          className="w-full text-left px-2.5 py-2 text-xs font-semibold hover:bg-zinc-50 rounded-xl transition text-zinc-700 flex items-center justify-between border-none bg-none cursor-pointer">
                          <span>{label}</span>
                          {sortCol === col && (
                            <span className="text-[10px] font-black text-[#6B8A5A]">{sortDir === 1 ? "ASC" : "DESC"}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* TABLE VIEW */}
            <AnimatePresence mode="wait">
              {view === "table" && (
                <motion.div key="table" className="ad-table" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>
                  {loading ? (
                    <BrandLoader 
                      message="Loading registered members..." 
                      subMessage="Fetching member accounts, streaks, and attendance" 
                      minHeight="min-h-[260px]" 
                    />
                  ) : filtered.length === 0 ? (
                    <div className="flex items-center justify-center py-16 text-[13px]" style={{ color:T.muted }}>
                      No users found
                    </div>
                  ) : (
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ width:32, padding:"10px 14px", borderBottom:`1px solid rgba(239,229,216,0.6)` }}>
                            <input type="checkbox"
                              checked={filtered.length > 0 && selected.size === filtered.length}
                              onChange={e => toggleAll(e.target.checked)}
                              className="w-4 h-4 rounded-[5px] cursor-pointer accent-[#6B8A5A]"
                              title="Select All Users"
                            />
                          </th>
                          {[
                            ["name","User", true],
                            ["country","Country", visibleColumns.country],
                            ["tier","Plan", visibleColumns.tier],
                            ["status","Status", visibleColumns.status],
                            ["sessions","Sessions", visibleColumns.sessions],
                            ["streak","Streak", visibleColumns.streak],
                            ["joined","Joined", visibleColumns.joined],
                            ["lastLogin","Last Login", visibleColumns.lastLogin]
                          ].filter(item => item[2]).map(([c,l]) => (
                            <TH key={c} col={c} label={l}/>
                          ))}
                          <th style={{ textAlign:"right", padding:"10px 14px", fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.9px", color:T.light, borderBottom:`1px solid rgba(239,229,216,0.6)` }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((u, i) => (
                          <motion.tr key={u.id}
                            initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay:i*0.03 }}
                            onClick={() => setDrawerUser(u)} className="cursor-pointer"
                            style={{ borderLeft:selected.has(u.id)?`3px solid ${T.olive}`:"3px solid transparent", transition:"all 0.2s" }}
                            onMouseEnter={e => e.currentTarget.style.background="rgba(107,138,90,0.04)"}
                            onMouseLeave={e => e.currentTarget.style.background=selected.has(u.id)?"rgba(107,138,90,0.06)":"transparent"}>
                            <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                              <input type="checkbox" checked={selected.has(u.id)}
                                onChange={e => { e.stopPropagation(); toggleSelect(u.id,e.target.checked); }}
                                onClick={e => e.stopPropagation()}
                                className="w-4 h-4 rounded-[5px] cursor-pointer accent-[#6B8A5A]"/>
                            </td>
                            <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                              <div className="flex items-center gap-2.5">
                                <UserAvatar user={u}/>
                                <div>
                                  <p className="text-[13px] font-bold text-[#1F1F1F]">{u.name}</p>
                                  <p className="text-[11px]" style={{ color:T.light }}>{u.email}</p>
                                </div>
                              </div>
                            </td>
                            {visibleColumns.country && (
                              <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`, fontSize:13, color:"#888" }}>
                                <span className="flex items-center gap-1">
                                  <FiGlobe size={13} className="text-zinc-400" /> {u.country}
                                </span>
                              </td>
                            )}
                            {visibleColumns.tier && (
                              <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <TierPill tier={u.tier} daysLeft={u.daysLeft} isExpired={u.isExpired}/>
                              </td>
                            )}
                            {visibleColumns.status && (
                              <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <StatusDot status={u.status}/>
                              </td>
                            )}
                            {visibleColumns.sessions && (
                              <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <p className="text-[13px] font-bold text-[#1F1F1F]">{u.sessions}</p>
                                <div className="h-1.5 rounded-full overflow-hidden mt-1" style={{ width:60, background:"rgba(239,229,216,0.6)" }}>
                                  <div className="h-full rounded-full" style={{ width:`${Math.min(u.sessions/2,100)}%`, background:`linear-gradient(90deg,${T.olive},${T.olive2})` }}/>
                                </div>
                              </td>
                            )}
                            {visibleColumns.streak && (
                              <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                                <span className="flex items-center gap-1 text-[13px] font-bold text-[#1F1F1F]">
                                  <FiFeather size={12} style={{ color:T.orange }}/>{u.streak}d
                                </span>
                              </td>
                            )}
                            {visibleColumns.joined && (
                              <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`, fontSize:12, color:T.muted }}>{u.joined}</td>
                            )}
                            {visibleColumns.lastLogin && (
                              <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`, fontSize:12, color:T.muted }}>{u.lastLogin}</td>
                            )}
                            <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`, position: "relative" }}>
                              <div className="flex gap-1.5 justify-end">
                                {[FiEye, FiBell, FiMoreHorizontal].map((Icon, j) => (
                                  <motion.button key={j} whileHover={{ y:-1 }}
                                    data-row-menu={j === 2 ? "" : undefined}
                                    onClick={e => {
                                      e.stopPropagation();
                                      if (j === 0) setDrawerUser(u);
                                      else if (j === 1) setNotifyUser(u);
                                      else if (j === 2) openRowMenu(e, u.id);
                                    }}
                                    className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer"
                                    style={{ background:"rgba(255,255,255,0.9)", borderColor:T.border, color: j === 1 ? T.orange : "#888", transition:"all 0.2s" }}
                                    title={j === 0 ? "View Profile" : j === 1 ? "Send Direct Notification" : "More Actions"}>
                                    <Icon size={13}/>
                                  </motion.button>
                                ))}
                              </div>

                              {menuUserId === u.id && menuPos && createPortal(
                                <div data-row-menu=""
                                  className="fixed w-44 rounded-2xl bg-white/95 border shadow-2xl p-2 z-[9999] flex flex-col gap-1 text-left"
                                  style={{ borderColor: "rgba(239,229,216,0.7)", backdropFilter: "blur(8px)", right: menuPos.right, top: menuPos.top, bottom: menuPos.bottom }}
                                  onClick={e => e.stopPropagation()}>
                                  <button onClick={() => { setDrawerUser(u); setMenuUserId(null); }}
                                    className="w-full text-left px-3.5 py-2 text-xs font-semibold hover:bg-zinc-50 rounded-xl transition text-zinc-700 flex items-center gap-2 border-none bg-none cursor-pointer">
                                    <FiEye size={12}/> View Profile
                                  </button>
                                  <button onClick={() => { setNotifyUser(u); setMenuUserId(null); }}
                                    className="w-full text-left px-3.5 py-2 text-xs font-semibold hover:bg-zinc-50 rounded-xl transition text-zinc-700 flex items-center gap-2 border-none bg-none cursor-pointer">
                                    <FiBell size={12} className="text-amber-500"/> Send Notification
                                  </button>
                                  <button onClick={async () => {
                                    setMenuUserId(null);
                                    try {
                                      const res = await adminApi.toggleUserStatus(u.id);
                                      if (res.data?.success || res.success) {
                                        showToast(`Status updated successfully.`);
                                        fetchUsers();
                                      }
                                    } catch (err) {
                                      showToast(err.message || "Failed to update status");
                                    }
                                  }}
                                    className="w-full text-left px-3.5 py-2 text-xs font-semibold hover:bg-zinc-50 rounded-xl transition text-zinc-700 flex items-center gap-2 border-none bg-none cursor-pointer">
                                    <FiCheck size={12}/> {u.isActive ? "Deactivate" : "Activate"}
                                  </button>
                                  <button onClick={() => {
                                    setMenuUserId(null);
                                    setDeleteConfirmUser(u);
                                  }}
                                    className="w-full text-left px-3.5 py-2 text-xs font-semibold hover:bg-red-50 hover:text-red-600 rounded-xl transition text-red-500 flex items-center gap-2 border-none bg-none cursor-pointer">
                                    <FiX size={12}/> Delete
                                  </button>
                                </div>,
                                document.body
                              )}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </motion.div>
              )}

              {/* CARD VIEW */}
              {view === "card" && (
                <motion.div key="cards" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} className="p-5">
                  {loading ? (
                    <BrandLoader 
                      message="Loading registered members..." 
                      subMessage="Fetching member accounts, streaks, and attendance" 
                      minHeight="min-h-[260px]" 
                    />
                  ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 mb-3 border border-amber-200">
                        <FiUsers size={24} />
                      </div>
                      <p className="text-sm font-bold text-zinc-800">No users found</p>
                      <p className="text-xs text-zinc-400 mt-1">Try adjusting your tier, search, or status filters.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                      {filtered.map((u, i) => (
                        <motion.div
                          key={u.id}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.03, duration: 0.28 }}
                          whileHover={{ y: -8, scale: 1.012 }}
                          className="group rounded-[24px] p-5 flex flex-col justify-between relative transition-all duration-300 overflow-hidden cursor-pointer hover:shadow-[0_24px_54px_-10px_rgba(107,138,90,0.22)] hover:border-[#6B8A5A] hover:bg-white border"
                          style={{
                            background: "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(255,252,246,0.92))",
                            borderColor: "rgba(239,229,216,0.85)",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.035)",
                          }}
                          onClick={() => setDrawerUser(u)}
                        >
                          {/* Sleek top glowing border on hover */}
                          <div
                            className="absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            style={{ background: "linear-gradient(90deg, #6B8A5A, #F29A0C)" }}
                          />

                          {/* Ambient radial gradient corner glow */}
                          <div
                            className="absolute top-0 right-0 w-36 h-36 rounded-full pointer-events-none opacity-30 group-hover:opacity-75 transition-opacity duration-300"
                            style={{ background: "radial-gradient(circle, rgba(107,138,90,0.18), transparent 70%)", filter: "blur(22px)" }}
                          />

                          <div>
                            {/* Header: Avatar + Tier + Status */}
                            <div className="flex items-start justify-between gap-2 mb-3.5 relative z-10">
                              <div className="relative flex-shrink-0">
                                <UserAvatar user={u} size={48} radius={15} />
                                {u.online && (
                                  <span
                                    className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white bg-emerald-500 shadow-sm"
                                    title="Active session online"
                                  />
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                <TierPill tier={u.tier} daysLeft={u.daysLeft} isExpired={u.isExpired} />
                                <StatusDot status={u.status} />
                              </div>
                            </div>

                            {/* User Identity Info */}
                            <div className="mb-3 relative z-10">
                              <h3
                                className="font-black text-[15px] text-[#1F1F1F] truncate group-hover:text-[#6B8A5A] transition-colors"
                                style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: "-0.3px" }}
                                title={u.name}
                              >
                                {u.name}
                              </h3>
                              <p className="text-[11px] font-medium text-zinc-400 truncate mt-0.5">
                                @{u.userName || `user_${u.id}`} · <span className="text-zinc-500">{u.country || "—"}</span>
                              </p>
                            </div>

                            {/* Contact Badges */}
                            <div className="flex flex-col gap-1.5 mb-3.5 text-[11px] text-zinc-600 relative z-10">
                              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-[#FAF6EE]/90 border border-[rgba(239,229,216,0.6)] truncate">
                                <FiMail size={12} className="text-[#6B8A5A] flex-shrink-0" />
                                <span className="truncate font-medium" title={u.email}>{u.email || "No email"}</span>
                              </div>
                              {u.phone && u.phone !== "—" && (
                                <div className="flex items-center gap-2 px-2.5 py-1 rounded-xl bg-white/70 border border-[rgba(239,229,216,0.45)] truncate">
                                  <FiPhone size={11} className="text-zinc-400 flex-shrink-0" />
                                  <span className="truncate text-zinc-500 font-medium">{u.phone}</span>
                                </div>
                              )}
                            </div>

                            {/* 3-Stat KPI Strip */}
                            <div className="grid grid-cols-3 gap-2 p-2.5 rounded-2xl bg-white/90 border border-[rgba(239,229,216,0.7)] shadow-sm mb-3.5 text-center relative z-10">
                              <div className="flex flex-col items-center">
                                <div className="flex items-center gap-1 text-[#6B8A5A]">
                                  <FiActivity size={12} />
                                  <span className="font-black text-[14px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                    {u.sessions || 0}
                                  </span>
                                </div>
                                <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 mt-0.5">Sessions</span>
                              </div>
                              <div className="flex flex-col items-center border-x border-zinc-100 px-1">
                                <div className="flex items-center gap-1 text-[#F29A0C]">
                                  <FiZap size={12} />
                                  <span className="font-black text-[14px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                    {u.streak || 0}d
                                  </span>
                                </div>
                                <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 mt-0.5">Streak</span>
                              </div>
                              <div className="flex flex-col items-center">
                                <div className="flex items-center gap-1 text-emerald-600">
                                  <FiTrendingUp size={12} />
                                  <span className="font-black text-[14px]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                                    {u.wellness ?? 0}%
                                  </span>
                                </div>
                                <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 mt-0.5">Wellness</span>
                              </div>
                            </div>

                            {/* Subscription Active Progress Linear Bar */}
                            <div className="mb-3.5 px-0.5 relative z-10">
                              <div className="flex items-center justify-between text-[11px] font-bold mb-1.5">
                                <span className="text-zinc-500 font-semibold flex items-center gap-1">
                                  <FiLayers size={11} className="text-[#6B8A5A]" /> Subscription Progress
                                </span>
                                <span className="text-[10.5px] font-bold text-[#6B8A5A]">
                                  {u.hasActiveSubscription
                                    ? (u.daysLeft > 0 ? `${u.daysLeft}d left` : "Active")
                                    : "Free Trial"} · {u.progress}%
                                </span>
                              </div>
                              <div className="h-2 rounded-full overflow-hidden bg-[#EFE5D8]/70 p-0.5 border border-[#EFE5D8]">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${Math.max(8, u.progress)}%`,
                                    background: u.hasActiveSubscription
                                      ? `linear-gradient(90deg, ${T.olive}, ${T.orange})`
                                      : `linear-gradient(90deg, #AAA, #CCC)`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Card Footer Actions */}
                          <div className="pt-3 border-t border-[rgba(239,229,216,0.6)] flex items-center justify-between gap-2 relative z-10">
                            <span className="text-[10px] font-semibold text-zinc-400 flex items-center gap-1">
                              <FiCalendar size={11} className="text-zinc-300" />
                              {u.joined || "Recent"}
                            </span>

                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => { e.stopPropagation(); setDrawerUser(u); }}
                                className="h-7 px-3 rounded-full text-[11px] font-bold text-[#6B8A5A] bg-[#6B8A5A]/10 hover:bg-[#6B8A5A]/20 border border-[#6B8A5A]/25 flex items-center gap-1 cursor-pointer transition-all"
                              >
                                <FiEye size={12} /> Profile
                              </motion.button>

                              <div className="relative">
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuUserId(menuUserId === u.id ? null : u.id);
                                  }}
                                  className="w-7 h-7 rounded-full flex items-center justify-center border border-[rgba(239,229,216,0.8)] bg-white hover:bg-zinc-50 text-zinc-600 cursor-pointer shadow-sm transition-all"
                                >
                                  <FiMoreHorizontal size={13} />
                                </motion.button>
                                {menuUserId === u.id && (
                                  <div
                                    className="absolute right-0 bottom-9 w-44 rounded-2xl bg-white/95 border border-[rgba(239,229,216,0.9)] shadow-2xl p-1.5 z-[100] flex flex-col gap-1 text-left no-scrollbar [&::-webkit-scrollbar]:hidden"
                                    style={{ backdropFilter: "blur(12px)", scrollbarWidth: "none", msOverflowStyle: "none" }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      onClick={() => { setDrawerUser(u); setMenuUserId(null); }}
                                      className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50 rounded-xl transition text-zinc-700 flex items-center gap-2 border-none bg-transparent cursor-pointer"
                                    >
                                      <FiEye size={12} className="text-[#6B8A5A]" /> View Details
                                    </button>
                                    <button
                                      onClick={() => { setNotifyUser(u); setMenuUserId(null); }}
                                      className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50 rounded-xl transition text-zinc-700 flex items-center gap-2 border-none bg-transparent cursor-pointer"
                                    >
                                      <FiBell size={12} className="text-amber-500" /> Send Notification
                                    </button>
                                    <button
                                      onClick={async () => {
                                        setMenuUserId(null);
                                        try {
                                          const res = await adminApi.toggleUserStatus(u.id);
                                          if (res.data?.success || res.success) {
                                            showToast(`Status updated successfully.`);
                                            fetchUsers();
                                          }
                                        } catch (err) {
                                          showToast(err.message || "Failed to update status");
                                        }
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50 rounded-xl transition text-zinc-700 flex items-center gap-2 border-none bg-transparent cursor-pointer"
                                    >
                                      <FiCheck size={12} className={u.isActive ? "text-amber-500" : "text-emerald-500"} />
                                      {u.isActive ? "Deactivate" : "Activate"}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setMenuUserId(null);
                                        setDeleteConfirmUser(u);
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-red-50 text-red-600 rounded-xl transition flex items-center gap-2 border-none bg-transparent cursor-pointer"
                                    >
                                      <FiTrash2 size={12} /> Delete User
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination — Smart Scalable Suite */}
            <div
              className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t"
              style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.4)" }}
            >
              <div className="text-[12px] font-bold text-zinc-500">
                Showing <span className="text-zinc-900">{totalUsers > 0 ? (currentPage - 1) * dynamicPageSize + 1 : 0}</span> to{" "}
                <span className="text-zinc-900">{pageSize === "all" ? totalUsers : Math.min(currentPage * dynamicPageSize, totalUsers)}</span> of{" "}
                <span className="text-zinc-900">{totalUsers}</span> users
              </div>

              {totalPages > 1 && (
                <div className="flex flex-wrap items-center gap-2">
                  {/* Active Page Counter Pill */}
                  <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
                    Page {currentPage} of {totalPages}
                  </span>

                  {/* Jump to First Page Button */}
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage(1)}
                    title="First Page"
                    className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                  >
                    <FiChevronsLeft size={14} />
                  </motion.button>

                  {/* Previous Page Button */}
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    title="Previous Page"
                    className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                  >
                    <FiChevronLeft size={14} /> Prev
                  </motion.button>

                  {/* Smart Windowed Page Number Buttons */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      let pages = [];
                      if (totalPages <= 7) {
                        pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                      } else if (currentPage <= 4) {
                        pages = [1, 2, 3, 4, 5, "...", totalPages];
                      } else if (currentPage >= totalPages - 3) {
                        pages = [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                      } else {
                        pages = [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
                      }

                      return pages.map((item, idx) => {
                        if (item === "...") {
                          return (
                            <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                              •••
                            </span>
                          );
                        }

                        const isActive = currentPage === item;
                        return (
                          <motion.button
                            key={item}
                            whileTap={{ scale: 0.92 }}
                            onClick={() => setCurrentPage(item)}
                            className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                              isActive
                                ? "text-white shadow-xs"
                                : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                            }`}
                            style={
                              isActive
                                ? {
                                    background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
                                    borderColor: "transparent",
                                    boxShadow: `0 4px 12px rgba(107,138,90,0.35)`,
                                  }
                                : {}
                            }
                          >
                            {item}
                          </motion.button>
                        );
                      });
                    })()}
                  </div>

                  {/* Next Page Button */}
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    title="Next Page"
                    className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
                  >
                    Next <FiChevronRight size={14} />
                  </motion.button>

                  {/* Jump to Last Page Button */}
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    title="Last Page"
                    className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
                  >
                    <FiChevronsRight size={14} />
                  </motion.button>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Profile drawer */}
        <AnimatePresence>
          {drawerUser && (
            <ProfileDrawer
              user={drawerUser}
              onClose={() => setDrawerUser(null)}
              showToast={showToast}
              onStatusToggled={handleStatusToggled}
              onNotify={(u) => setNotifyUser(u)}
              onUserDeleted={(deletedId) => {
                setUsers((prev) => prev.filter((u) => u.id !== deletedId));
                setDrawerUser(null);
                fetchUsers();
              }}
            />
          )}
        </AnimatePresence>

        {/* Direct Notification Modal */}
        <AnimatePresence>
          {notifyUser && (
            <DirectNotificationModal
              user={notifyUser}
              onClose={() => setNotifyUser(null)}
              showToast={showToast}
            />
          )}
        </AnimatePresence>

        {/* Add user modal */}
        <AnimatePresence>
          {showAdd && (
            <AddUserModal
              onClose={() => setShowAdd(false)}
              onCreated={fetchUsers}
              showToast={showToast}/>
          )}
        </AnimatePresence>



        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div key={toast} initial={{ opacity:0, y:16, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }} exit={{ opacity:0, y:16 }}
              className="ad-toast fixed bottom-7 right-7 z-[200] flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
              style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)", boxShadow:"0 16px 48px rgba(0,0,0,0.25)", fontFamily:"'Inter',sans-serif" }}>
              <FiCheck size={16} style={{ color:T.olive }}/>{toast}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirmUser && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[1000] flex items-center justify-center p-6"
              style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(12px)" }}
              onClick={() => setDeleteConfirmUser(null)}>
              <motion.div initial={{ scale: 0.88, y: 20 }} animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.88 }} transition={{ type: "spring", stiffness: 320, damping: 28 }}
                onClick={e => e.stopPropagation()}
                className="w-full max-w-sm rounded-[32px] overflow-hidden"
                style={{ background: "#fff", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}>
                
                <div className="p-7 text-center">
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5 border border-red-100">
                    <FiAlertTriangle size={28} className="text-red-500" />
                  </div>
                  <h2 className="font-black text-[20px] mb-2 text-[#1F1F1F]"
                    style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
                    Delete User Account
                  </h2>
                  <p className="text-[13px] leading-[1.7] mb-6 text-zinc-500">
                    Are you sure you want to permanently delete the user account for <strong className="text-zinc-800">"{deleteConfirmUser.name}"</strong>? This action is irreversible.
                  </p>
                  <div className="flex flex-col gap-2">
                    <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}
                      onClick={async () => {
                        const targetUser = deleteConfirmUser;
                        setDeleteConfirmUser(null);
                        try {
                          const res = await adminApi.deleteUser(targetUser.id);
                          if (res.data?.success || res.success) {
                            showToast(`Successfully deleted user: ${targetUser.name}`);
                            fetchUsers();
                          }
                        } catch (err) {
                          showToast("Failed to delete user");
                        }
                      }}
                      className="w-full h-12 rounded-full font-bold text-[14px] text-white cursor-pointer border-none shadow-lg"
                      style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)", boxShadow: "0 8px 20px rgba(239,68,68,0.25)", fontFamily: "'Inter',sans-serif" }}
                    >
                      Yes, Delete Account
                    </motion.button>
                    <button onClick={() => setDeleteConfirmUser(null)} 
                      className="w-full h-12 rounded-full font-semibold text-[13px] text-zinc-500 hover:bg-zinc-100 transition-colors border-none bg-none cursor-pointer"
                      style={{ fontFamily: "'Inter',sans-serif" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminDashboardLayout>
  );
}